/* The receiver is the only source of truth. This file talks to it and holds no
 * rules of its own.
 *
 * Same-origin throughout: the SPA is served by the same Caddy site that proxies
 * /v1, so the session cookie rides along without `credentials` gymnastics and
 * there is no CORS anywhere. The one deliberate exception is object storage,
 * which the browser fetches directly with a presigned URL -- that never comes
 * through here.
 */
import { currentZone, zoneLabel } from './time'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail?: string,
  ) {
    super(detail ? `${code}: ${detail}` : code)
  }
}

/* A 401 can surface from any request at any depth. Rather than teaching every
 * caller to handle it, the shell listens for this and shows the login screen --
 * so an expired session is one redraw, not a page full of error boxes. */
export const UNAUTHORIZED = 'phoniq:unauthorized'

/* No request may hang for ever. A fetch with no timeout that never settles
 * leaves the page on its loading skeleton with nothing to click and nothing to
 * read -- indistinguishable from a broken build, and the one failure mode a
 * user cannot report usefully because there is no error to copy. Twenty seconds
 * is far longer than any endpoint here should take and far shorter than a
 * person's patience. */
const TIMEOUT_MS = 20_000

async function req<T>(path: string, init?: RequestInit,
                       timeoutMs: number = TIMEOUT_MS): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      signal: AbortSignal.timeout(timeoutMs),
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    })
  } catch (e) {
    const timedOut = e instanceof DOMException && e.name === 'TimeoutError'
    throw new ApiError(
      0,
      timedOut ? 'timeout' : 'unreachable',
      timedOut
        ? `The receiver did not answer within ${timeoutMs / 1000}s.`
        : 'Could not reach the receiver. Check the connection and reload.',
    )
  }

  if (res.status === 204) return undefined as T

  let body: any = null
  try {
    body = await res.json()
  } catch {
    /* An HTML error page from the proxy, or an empty body. Fall through to the
       status-based error below rather than throwing a JSON parse error, which
       would name the wrong problem. */
  }

  if (!res.ok) {
    // The receiver normalises every error to {schema, error, detail}
    // (app/main.py). Render `error`; never parse `detail` for meaning.
    const err = new ApiError(
      res.status,
      body?.error ?? `http_${res.status}`,
      body?.detail,
    )
    if (res.status === 401) window.dispatchEvent(new CustomEvent(UNAUTHORIZED))
    throw err
  }
  return body as T
}

export const api = {
  get: <T,>(path: string) => req<T>(path),
  /** `timeoutMs` only for the rare write that is slow by nature -- a printed
   *  book is set by a browser on the server -- never to hide a slow endpoint. */
  post: <T,>(path: string, body?: unknown, timeoutMs?: number) =>
    req<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }, timeoutMs),
  patch: <T,>(path: string, body: unknown, etag?: string) =>
    req<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: etag ? { 'If-Match': etag } : {},
    }),
  put: <T,>(path: string, body: unknown, etag?: string) =>
    req<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: etag ? { 'If-Match': etag } : {},
    }),
  del: <T,>(path: string) => req<T>(path, { method: 'DELETE' }),
}

export function qs(params: Record<string, string | number | boolean | null | undefined>) {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

/* ----------------------------------------------------------------- types */

export type MemberRole =
  | 'company_manager' | 'area_manager' | 'site_manager' | 'ship' | 'subsupplier'

export interface Me {
  user_id: string | null
  label: string
  email: string | null
  display_name: string | null
  /** The PLATFORM role: what kind of action is possible at all. Unrelated to
   *  which company's rows are visible, which is `orgs` below. */
  role: 'viewer' | 'researcher' | 'operator' | 'admin'
  org_ids: string[]
  /** Which credential answered. `device` is a field device: no account, no
   *  address, and a capability set already narrowed server-side. The SPA
   *  branches on this rather than inferring it from a missing email. */
  via: 'session' | 'token' | 'device'
  /** Unchanged meaning: Phoniq staff who may change the fleet. Every existing
   *  call site reads this and none of them had to move when customers arrived.
   *  For anything a customer may do, read `can` instead. */
  can_write: boolean

  /* Everything below is additive and optional, so this bundle keeps working
   * against a receiver that predates it -- the two deploy independently, and
   * `last_upload_at` beside `last_seen_at` on FleetNode is the precedent.
   * Read them with a default, never bare. */

  /** Every feature key live on any farm this person can see, unioned.
   *
   *  Distinct from `can`, and the distinction is load-bearing: `can` is role
   *  verbs — what kind of action is permitted — while a feature is what was
   *  purchased. The left-hand menu needs the second, because an item for a
   *  product nobody bought is an item that answers 403.
   *
   *  Optional, like everything below, so this bundle keeps working against a
   *  receiver that predates it. Read with a default, never bare. */
  features?: string[]

  is_staff?: boolean
  /** Present only when `via` is 'device', on the same precedent as
   *  `viewing_as`: a key that exists only when it means something, so a reader
   *  cannot mistake a default for an answer.
   *
   *  Gate() needs both to pick a screen. An approved, NAMED device is a
   *  customer with a narrower credential and gets the product; anything else
   *  gets the device's own screen, which is the only one with a sentence for
   *  waiting or for being unnamed.
   *
   *  Do not infer this from `orgs.length === 0`. It works today, because a
   *  pending device holds no membership — and it is the kind of inference that
   *  silently starts admitting the wrong thing the day that stops being true. */
  device?: {
    approved: boolean
    needs_name: boolean
  } | null
  orgs?: { org_id: string; name: string; member_role: MemberRole }[]
  /** Named verbs, derived server-side from the membership role and (from
   *  stage 3) the site's product grant. Scope is checked separately: these say
   *  what kind of action is allowed, not which rows it may touch. */
  can?: {
    listen: boolean
    clip: boolean
    archive: boolean
    /** Send a clip to somebody with no account here. */
    share: boolean
    /** Record a lice count, which re-runs the model. Not given to a vessel or
     *  a contractor: a shared account in a wheelhouse should not write a
     *  number a treatment decision rests on. */
    lice_count: boolean
    manage_people: boolean
    manage_fleet: boolean
    /** Make and order the printed Lusedagbok. Absent on a receiver that
     *  predates it, which hides the product rather than showing a page that
     *  answers 404. */
    lice_book?: boolean
    /** Number and draw the farm's rings (PUT /layout). */
    layout_edit?: boolean
  }
  /** Set only while a staff member is previewing a customer's portal. While
   *  it is set, `is_staff` is false and every capability is false: the
   *  preview is read-only, and the whole point is that the scoping and the
   *  entitlements bite exactly as they would for the customer. */
  viewing_as?: { org_id: string; org_name: string; until: string } | null
}

/* ------------------------------------------------------- field devices */

/* A device is not a smaller account. It has no address and no password, it is
 * admitted by exchanging an enrollment code once, and its identity IS the
 * browser it enrolled on -- so everything below is addressed by cookie rather
 * than by any id the client holds. Nothing here sends a device id anywhere: an
 * id in a request body would be a client-asserted identity claim, which is the
 * thing the cookie exists to avoid.
 */

export interface DeviceMe {
  device_id: string
  /** What somebody typed on this device. Null until they do. */
  display_name: string | null
  /** The label on the code it enrolled with -- "Merd 3", "Lokalitet Nord".
   *  Stands in on screen until a name is typed, so an unnamed tablet still
   *  reads as something rather than as a database key. */
  code_label: string | null
  needs_name: boolean
  org_id: string
  org_name: string
  member_role: MemberRole
  expires_at: string | null
  enrolled_at: string
  can: NonNullable<Me['can']>
  sites: { site_id: string; name: string }[]

  /** Whether the grant IN FORCE exists. False means this device is waiting and
   *  can do nothing at all -- `sites` is empty and every verb in `can` is
   *  false, because a waiting device has no membership, not because a flag was
   *  checked somewhere. */
  approved: boolean
  approved_via: 'approval' | 'link' | 'network' | 'window' | 'legacy' | null
  /** Whether a grant has been ASKED for and not yet given. Separate from
   *  `approved`, and both can be true at once: a tablet counting at Ljosoy
   *  whose operator has just typed the company-wide code is approved AND
   *  waiting. Collapsing them would mean re-scoping stopped a working device
   *  mid-shift. */
  waiting: boolean
  /** What it is waiting FOR, in words. Null when it is not. */
  waiting_for: string | null
}

export interface Enrolled {
  device_id: string
  org_id: string
  org_name: string
  member_role: MemberRole
  display_name: string | null
  /** Where this code put the device, in words: a farm, an area, or the company. */
  scope: string
  needs_name: boolean
  code_label: string | null
  /** True when the code widened or moved an EXISTING device rather than
   *  admitting a new one. The device keeps its id and its history, which is
   *  the whole reason re-enrolling is a POST with the cookie still attached
   *  rather than a sign-out followed by a sign-in. */
  rescoped: boolean
  /** True when a PERSON was signed in on this browser and has been signed out.
   *  Said out loud rather than done quietly: they will want to know why. */
  signed_out: boolean
  /** Whether this device may actually do anything yet.
   *
   *  The code is short and memorable, which is the point and which also means
   *  it is guessable -- so typing it correctly is not what gets a device in.
   *  Two things do: enrolling from a network the company has registered, or a
   *  company manager saying yes. False here means the second, and the screen
   *  goes to the waiting room rather than to the counter. */
  approved: boolean
  /** 'network' when a registered address admitted it and nobody was asked.
   *  Null when it is waiting. */
  admitted_by: 'network' | 'window' | null
  pending: boolean
}

/** Exchange a code for a session. The one unauthenticated call in this file.
 *
 *  Sent with the cookie attached, always. If a device session is already
 *  present and the code belongs to the same company, the server rescopes this
 *  device in place instead of minting a second one -- which is what keeps last
 *  month's counts attached to the tablet that entered them.
 */
export function enrollDevice(code: string, displayName?: string) {
  return api.post<Enrolled>('/v1/device/enroll', {
    code,
    ...(displayName ? { display_name: displayName } : {}),
  })
}

export function deviceMe() {
  return api.get<DeviceMe>('/v1/device/me')
}

/** "Hvem er du?", answered on the device itself.
 *
 *  A label, not a claim: nothing verifies it and nothing depends on it. It
 *  exists so that a manager reading a list of tablets can tell one from
 *  another, which is what makes unrolling one a decision somebody can take.
 */
export function renameDevice(displayName: string) {
  return api.patch<{ display_name: string }>('/v1/device/me', {
    display_name: displayName,
  })
}

/** "Meld ut denne enheten" -- for a tablet being sold or handed on.
 *
 *  Explicitly destructive and deliberately separate from re-enrolling: the
 *  next enrollment on this browser is a NEW device with a new id. Correct,
 *  because the cookie is the only honest evidence of continuity there is.
 */
export function deviceSignout() {
  return api.post<{ ok: true }>('/v1/device/signout')
}

/* ------------------------------------------------ devices, from the office */

export interface FieldDevice {
  device_id: string
  display_name: string | null
  /** The label on the code it came in with. What a device is called on this
   *  screen when nobody has typed a name on it. */
  code_label: string | null
  code_id: string | null
  member_role: MemberRole
  expires_at: string | null
  enrolled_at: string
  last_seen_at: string | null
  last_ip: string | null
  user_agent: string | null
  /** Where it may count. EMPTY means the whole company -- exactly as it does
   *  for a person, because it is the same membership_scopes rows. */
  scopes: { kind: 'area' | 'site'; id: string; name: string | null }[]

  /** How it got in. The question asked about a device that turns out not to
   *  belong, and the reason this is on the row rather than in a log. */
  approved_at: string | null
  approved_via: 'approval' | 'link' | 'network' | 'window' | 'legacy' | null
  /** The manager who approved it, when a signed-in one did. Null for the link
   *  in the notification mail, which we can only attribute to whoever could
   *  read that inbox, and null for a network, which nobody decided. */
  approved_by_name: string | null
  /** The name of the network that admitted it, when one did. */
  approved_network: string | null

  /** Asking for something and not yet granted it. A device can be BOTH
   *  connected and waiting: already counting at one farm, asking to be widened
   *  to more. The two flags are separate because collapsing them would mean a
   *  screen that had to stop a working tablet to show a pending request. */
  waiting: boolean
  waiting_code_id: string | null
  waiting_code_label: string | null
  waiting_for: string | null
  /** Has a membership here, i.e. is actually working. */
  connected: boolean
}

/** A network a company has registered as its own.
 *
 *  A device enrolling from inside one is admitted immediately and nobody is
 *  asked. It is the PUBLIC IP and never the SSID -- no browser exposes the
 *  network name to a page, deliberately -- which makes it real for an office
 *  wifi behind one fixed address and worthless on mobile data, where
 *  carrier-grade NAT puts a phone on the walkway and a phone in Oslo in the
 *  same pool. That is why it is a shortcut past approval and never a
 *  replacement for it.
 */
export interface EnrollNetwork {
  net_id: string
  /** The address alone, and the prefix length, for a screen that wants to show
   *  "84.208.44.17" rather than "84.208.44.17/32". */
  address: string
  masklen: number
  /** Normalised by the database: what is actually matched against. */
  net: string
  label: string | null
  created_at: string
  created_by_name: string | null
  last_used_at: string | null
  revoked_at: string | null
  /** Devices this network let in. They are untouched if it is withdrawn. */
  devices: number
  live: boolean
}

export interface EnrollCode {
  code_id: string
  label: string | null
  member_role: MemberRole
  area_id: string | null
  site_id: string | null
  /** The farm's or area's name, already resolved. Null for a company code. */
  scope_name: string | null
  /** Null is unlimited: the card a crew shares. */
  max_uses: number | null
  uses: number
  membership_days: number | null
  expires_at: string
  revoked_at: string | null
  created_at: string
  last_used_at: string | null
  /** How many devices came in on this code and are still enrolled. The number
   *  a confirmation dialog needs, so it can say that revoking disturbs none
   *  of them. */
  devices: number
  /** Not revoked, not expired, not used up. One field rather than three
   *  conditions repeated on screen. */
  live: boolean
  /** The code itself. An earlier version of this screen deliberately withheld
   *  it, because the code was sixty random bits and a list showing it was a
   *  list worth stealing. The code is now something a manager chose to be
   *  memorable, which makes it guessable, which is why getting in is gated on
   *  a registered network or a person's approval instead. Once entropy is not
   *  the lock, hiding the code protects nothing and costs the one question the
   *  old screen could not answer for anybody: what did I set for Ljosoy? */
  code: string | null
  display_code: string | null
}

/** A code, in the clear. Readable again afterwards from the code list.
 *
 *  What comes back is the FOLDED form, and it can differ from what was typed:
 *  the alphabet has no I and no O, so they are read as 1 and 0. A manager who
 *  types "Ljo2" gets "LJ02", and needs to see that before printing the card --
 *  which is the main reason this response carries the code at all rather than
 *  just an id.
 */
export interface MintedCode {
  code_id: string
  max_uses: number | null
  membership_days: number | null
  expires_at: string
  code: string
  /** Grouped in fours, for reading aloud and copying onto a card. */
  display_code: string
  /** The link the QR encodes. The code rides in the FRAGMENT, which is never
   *  sent to a server -- so it cannot reach an access log or a Referer header,
   *  and a corporate link scanner following every URL in an inbox cannot spend
   *  it. */
  qr_url: string
}

/* The two lists are read through `useApi` by path rather than through a helper
 * here, because they are the only thing on that screen that benefits from the
 * cache: a manager who rotates a code and comes back should see the list
 * without a spinner over data that is two seconds old. The writes below go
 * through helpers, because a write has nothing to cache and everything to name.
 *
 *   GET /v1/orgs/{id}/devices       -> { items: FieldDevice[] }
 *   GET /v1/orgs/{id}/enroll-codes  -> { items: EnrollCode[] }
 */

export function renameFieldDevice(orgId: string, deviceId: string, displayName: string) {
  return api.patch<{ display_name: string }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/devices/${encodeURIComponent(deviceId)}`,
    { display_name: displayName })
}

/** Stop a device. It ends at the device's very next request, and everything it
 *  recorded stays exactly where it is, still attributed to it. */
export function unrollDevice(orgId: string, deviceId: string) {
  return api.post<{ ok: true }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/devices/`
    + `${encodeURIComponent(deviceId)}:unroll`)
}

export function mintEnrollCode(orgId: string, body: {
  /** What the manager typed. Three to sixteen characters, folded on the way
   *  in. Omitting it asks the server for a random one, which is what :rotate
   *  does -- it must not invent a name on the manager's behalf. */
  code?: string
  member_role: MemberRole
  area_id?: string | null
  site_id?: string | null
  label?: string
  days?: number | null
  membership_days?: number | null
}) {
  return api.post<MintedCode>(
    `/v1/orgs/${encodeURIComponent(orgId)}/enroll-codes`, body)
}

/** Replace the characters, keep everything else. Devices already enrolled with
 *  the old code are untouched, because a code is spent at enrollment and is
 *  never read again on any other request. That is what makes replacing a lost
 *  card an afternoon's work rather than a round of phone calls. */
export function rotateEnrollCode(orgId: string, codeId: string) {
  return api.post<MintedCode & { devices_unaffected: number }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/enroll-codes/`
    + `${encodeURIComponent(codeId)}:rotate`)
}

/** The open door, as GET /v1/orgs/{id}/enroll-codes reports it alongside the
 *  codes themselves.
 *
 *  On the codes response rather than an endpoint of its own: the window is a
 *  fact about how a code gets redeemed, and the codes screen is where somebody
 *  is standing when they decide to open one. A fourth request would let the
 *  door's state and the codes it applies to be one round trip apart on screen. */
export interface EnrollWindowState {
  open: boolean
  /** When it shuts. Null whenever `open` is false, including when it lapsed
   *  rather than being closed by hand -- an expired window is a shut one. */
  open_until: string | null
  /** Who propped it open. Null once it is shut: "who opened it last Tuesday"
   *  is the audit log's question, and answering it on a closed door would make
   *  the door look attended. */
  opened_by: string | null
  /** The longest a single open may run, so the form does not have to restate
   *  a number the receiver owns. */
  max_hours: number
}

/** Let devices in without asking anybody, for the next few hours.
 *
 *  Re-opening REPLACES rather than extends: four hours means shut four hours
 *  from now, whatever it said before -- including when that shortens it, which
 *  is what somebody wanting it shut sooner will expect.
 *
 *  `admitted` and `skipped` name the devices that were ALREADY waiting when
 *  the door opened. They are named rather than counted because letting in a
 *  device somebody had deliberately left in the queue is a thing the manager
 *  should see they just did. `skipped` are the ones whose code has since been
 *  revoked -- opening a window never resurrects a withdrawn code. */
export function openEnrollWindow(orgId: string, hours: number) {
  return api.post<EnrollWindowState & {
    admitted: string[]
    skipped: string[]
  }>(`/v1/orgs/${encodeURIComponent(orgId)}/enroll-window`, { hours })
}

/** Shut it now. Devices already admitted are untouched -- what got a device in
 *  is never what keeps it in. Shutting a door that is already shut succeeds,
 *  because the person pressing it wants it shut and it is. */
export function closeEnrollWindow(orgId: string) {
  return api.del<EnrollWindowState>(
    `/v1/orgs/${encodeURIComponent(orgId)}/enroll-window`)
}

export function revokeEnrollCode(orgId: string, codeId: string) {
  return api.del<{ ok: true; devices_unaffected: number }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/enroll-codes/`
    + `${encodeURIComponent(codeId)}`)
}

/* --------------------------------------------------------------- approving */

/** What an approval link is about, before anybody decides anything.
 *
 *  A POST because the token arrives in the URL fragment and has to be handed
 *  back in a body -- a fragment is never sent to a server, which is what keeps
 *  a corporate mail scanner from spending the link before the manager reads
 *  it. It is also why approval cannot happen on page load: the click that
 *  matters is a person looking at which device, which farm, and from where.
 */
export function peekApproval(token: string) {
  return api.post<{
    device_id: string
    display_name: string | null
    enrolled_at: string
    org_id: string
    org_name: string
    member_role: MemberRole
    scope: string
    code_label: string | null
    /** Already counting somewhere and asking to be widened. A different
     *  sentence on screen, because declining it does not mean the same thing. */
    rescope: boolean
    ip: string | null
    user_agent: string | null
    asked_at: string | null
  }>('/v1/device/approval/peek', { token })
}

export function decideApproval(token: string, action: 'approve' | 'decline') {
  return api.post<{ ok: true; action: 'approve' | 'decline' }>(
    '/v1/device/approval', { token, action })
}

/** The same decision, from the device list, with a session behind it. */
export function approveDevice(orgId: string, deviceId: string) {
  return api.post<{ ok: true }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/devices/`
    + `${encodeURIComponent(deviceId)}:approve`)
}

/** Turn a waiting device away. Separate from unrolling on purpose: unrolling
 *  stops something that was working, this refuses something that never got in,
 *  and a screen offering "unroll" for a device that was never enrolled would
 *  be describing the wrong event. */
export function declineDevice(orgId: string, deviceId: string) {
  return api.post<{ ok: true }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/devices/`
    + `${encodeURIComponent(deviceId)}:decline`)
}

/* ---------------------------------------------------------- the networks */

/** The registered networks, and the address you are asking from.
 *
 *  `your_ip` comes back so the screen can offer "add the one I am on" without
 *  a second round trip, and so a manager can see whether they are on the
 *  network they think they are.
 */
export function listNetworks(orgId: string) {
  return api.get<{
    your_ip: string | null
    your_network: string | null
    items: EnrollNetwork[]
  }>(`/v1/orgs/${encodeURIComponent(orgId)}/networks`)
}

/** Omitting `net` registers the address this request came from -- which is the
 *  whole ergonomics of the feature. A manager standing on the office wifi
 *  presses one button and what gets registered is the address we actually saw,
 *  not one they typed from memory, which is the version that silently
 *  registers the wrong network. */
export function addNetwork(orgId: string, body: { net?: string; label?: string }) {
  return api.post<{ net_id: string; net: string }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/networks`, body)
}

export function revokeNetwork(orgId: string, netId: string) {
  return api.del<{ ok: true; net: string; devices_unaffected: number }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/networks/`
    + `${encodeURIComponent(netId)}`)
}

/** Start a password reset for somebody else in your company.
 *
 *  The link goes to the address on file and nothing comes back here -- no
 *  token, no address, no password. A manager can ask the owner of an account
 *  to choose a new password; they cannot choose, see or repeat one.
 */
export function resetMemberPassword(orgId: string, userId: string) {
  return api.post<{ ok: true }>('/v1/people/reset-password',
    { org_id: orgId, user_id: userId })
}

/* ------------------------------------------------------------------ merder */

/* Vedlegg 1 to lakselusforskriften is written per ring — ten random fish from
 * EVERY merd, twenty in the spring window, journalled per fish in three stage
 * groups — while the report to Mattilsynet is the locality mean. So the
 * regulatory number is a lossy projection of what the farm is already obliged
 * to hold, and a spreadsheet keeps the projection and discards the observation.
 *
 * These types are the observation. The site figure is derived from them and
 * `lice_counts` keeps meaning exactly what it meant, which is what let this
 * ship without touching the index every count in production upserts on.
 */

/** A ring. Physical, numbered, and it does not move.
 *
 *  `pen_no` is the position in the array, not the hardware: a ring lifted out
 *  after storm damage and replaced is still merd 3, and a count from before the
 *  swap is still a count of merd 3. It is text because "3A", "M7" and "N2" all
 *  occur on real localities.
 */
export interface Pen {
  pen_id: string
  pen_no: string
  name: string | null
  circumference_m: number | null
  depth_m: number | null
  /** Walking order. "10" sorts before "2" as a string, and a counting sheet in
   *  the wrong order is a sheet somebody transcribes wrong. */
  sort: number
  /** Null for every farm nobody has drawn yet, which is the ordinary answer
   *  and not an error. The map reads it as "not placed". */
  lat?: number | null
  lon?: number | null
  shape?: PenShape
  /** Degrees clockwise from north, and only ever on a square: a circle
   *  carrying a rotation is a claim about nothing, and the database refuses
   *  it. */
  bearing_deg?: number | null
}

/** One ring on one day — or a ring with nothing recorded yet.
 *
 *  Rings with no count for the day come back too, with `pen_count_id` null.
 *  "Merd 5 has not been counted" is the answer the page most needs and an
 *  absent row cannot give it.
 */
export interface PenCount {
  pen_id: string
  pen_no: string
  name: string | null
  /** True for a ring that has since been taken out of the array and is in this
   *  day's list only because it was counted on it.
   *
   *  Those fish were looked at and the count was filed, so the site figure
   *  still includes them — retiring a ring must not silently rewrite a number
   *  somebody already reported. The row is here so the two agree. */
  retired?: boolean
  /** Null when nothing has been recorded at this ring today. */
  pen_count_id: string | null
  adult_female: number | null
  mobile: number | null
  sessile: number | null
  n_fish: number | null
  temp_c: number | null
  note: string | null
  /** 'entered' — somebody typed four numbers, which is a complete and
   *  legitimate count. 'derived' — computed from the individual fish, which is
   *  the only shape that can carry a spread. */
  basis: 'entered' | 'derived' | null
  /** Sample variance of the per-fish adult-female counts. Present only where
   *  the fish were written down, enforced by a database constraint rather than
   *  by a handler — so a variance beside a typed mean is not a thing that can
   *  exist. It is what separates "merd 3 is over the limit" from "merd 3 had
   *  one fish with four lice on it". */
  adult_female_var: number | null
  /** Why a ring was skipped. Mattilsynet asks; silence is not an answer, and a
   *  missing row cannot become one later. */
  not_counted_reason: string | null
  recorded_at: string | null
  counted_by: string | null
  counted_by_kind: 'person' | 'device' | null
  fish_rows: number
  /** Only from the single-day endpoint, never from the write response. */
  fish?: FishCount[]
}

export interface FishCount {
  fish_no: number
  adult_female: number | null
  mobile: number | null
  sessile: number | null
  weight_g: number | null
}

/** What the farm's day became after a ring was written.
 *
 *  Rolled up server-side inside the write transaction, so this is never stale
 *  by even one request — and it is FISH-WEIGHTED, not the mean of merd means.
 *  In the compliant case the two agree; they diverge when a ring was
 *  short-sampled, and then the fish-weighted mean is the one that equals "lice
 *  found ÷ fish looked at", which is what the limit is expressed against.
 */
export interface PenRollup {
  adult_female?: number | null
  mobile?: number | null
  sessile?: number | null
  n_fish?: number | null
  basis?: 'site' | 'pens' | 'fish'
  pens_counted: number
  /** Null when the rings were sampled unevenly — then there is no honest
   *  scalar to send as `fiskPerMerd`. */
  fish_per_pen?: number | null
  /** Standard error of the site mean, WITHIN-RING ONLY. Present only when every
   *  contributing ring wrote its fish down. Between-ring clustering is what
   *  dominates and is a fleet-wide job for the model, not something to guess
   *  from eight rings on one Tuesday — so this is honestly incomplete in a
   *  known direction rather than wrong in an unknown one. */
  adult_female_se?: number | null
}

/** Where a ring floats, and what shape it is.
 *
 *  There is no radius. `circumference_m` is already here, already filled in,
 *  and is the number the industry actually speaks -- a farm has "160-ringer",
 *  nobody says "radius 25,5 m". The footprint is derived from it, so the
 *  polygon on the map and the volume a delousing is priced against are one
 *  fact rendered twice rather than two copies that drift.
 */
export type PenShape = 'circle' | 'square'

export interface Structure {
  structure_id: string
  /** Open vocabulary rather than a boolean, because the next thing anyone
   *  wants on the picture is a mooring line and then the feed pipes. */
  kind: 'barge' | 'boat' | 'walkway' | 'mooring' | 'other'
  name: string | null
  lat: number
  lon: number
  length_m: number | null
  width_m: number | null
  bearing_deg: number | null
}

export interface SiteLayout {
  site: {
    site_id: string
    name: string
    lat: number | null
    lon: number | null
    locality_no: number | null
    municipality: string | null
  }
  pens: Pen[]
  structures: Structure[]
}

export function siteLayout(siteId: string) {
  return api.get<SiteLayout>(
    `/v1/sites/${encodeURIComponent(siteId)}/layout`)
}

/* ------------------------------------------------------------------ strøm */

/** How the water moves through the array, hour by hour.
 *
 *  A FORECAST AND NOT A MEASUREMENT, and every field below exists to keep that
 *  distinction visible. The chain is marine.earth.gl -> phoniq-flow ->
 *  the receiver -> here; nothing in it was measured at the farm, and the
 *  hydrophone -- which measures a great many things -- does not measure this.
 *
 *  ONE VECTOR PER FARM. The upstream current model's grid is about a kilometre
 *  across and an array is half of one, so every ring gets the same speed and
 *  the same bearing. What differs between rings is geometry: which of them the
 *  water reaches first. There is no per-cage current and this type deliberately
 *  has no shape for one.
 */
export interface FlowMark {
  pen_no: string
  /** The water is barely moving. `length_r` is then null and the map draws a
   *  slack mark, because a two-pixel arrow is a direction nobody can see they
   *  are being asked to trust. */
  slack: boolean
  /** Bearings from the ring's own centre, degrees clockwise from north. */
  upstream_bearing: number
  downstream_bearing: number
  /** Both in multiples of the ring's radius, so the arrow scales with the
   *  cage at every zoom. The scale lives with the service that justifies it. */
  standoff_r: number
  length_r: number | null
  /** How much new water this cage is getting. Not a judgement about whether it
   *  is ENOUGH — that depends on the biomass in the ring, and is the ranking's
   *  job. Deliberately not a more-is-better ladder: `strong` is a caution. */
  exchange: 'slack' | 'weak' | 'moderate' | 'good' | 'strong' | null
}

/** How well each cage is flushed in one hour, RELATIVE TO THE OTHERS HERE.
 *
 *  A RANKING AND NEVER A CONCENTRATION, and the reason is worth keeping close
 *  to the type. Three of the inputs are guesses — salinity is not published
 *  anywhere on this coast, the net's blockage was never measured at this farm,
 *  and the metabolic curve is for salmon in general. All three are common
 *  multipliers: they scale every cage at the site together, so they move the
 *  whole set and do not reorder it. The ordering survives the constants; a
 *  mg/L would not, and setting one in a typeface that implies two decimals
 *  would be the most expensive kind of wrong. */
export interface FlowOxygenPen {
  pen_no: string
  /** 0 is the best-flushed cage at this farm in this hour, 1 the worst. It says
   *  nothing about whether either is good — only which is which. */
  score: number
  rank: number
  /** Which cages this one stands behind, nearest first. `share` is how much of
   *  their drawdown still reaches it after the water's transit time; at the
   *  speeds this coast reports it is usually small, and saying so is the
   *  point. */
  upstream: { pen_no: string; distance_m: number; share: number }[]
}

export interface FlowOxygen {
  pens: FlowOxygenPen[]
  /** Non-null when nothing could be ranked, and it is a real answer rather
   *  than a gap: slack water is the hour a farm manager most wants to know
   *  about, and the model that ranks cages by flushing has nothing to say when
   *  there is no flushing. */
  reason: string | null
  /** Whether the farm was feeding, and how well that is known.
   *
   *  `measured`   the hydrophone heard the blower, or heard it stay quiet
   *  `uncertain`  something was in the way of hearing it — usually a vessel
   *               alongside, which is louder than a blower and sits in the
   *               same band. NOT evidence the feeder stopped, and the hour is
   *               flagged rather than blanked, exactly as the feeding index
   *               treats a day with a rig on the site.
   *  `expected`   a future hour, from this farm's own habitual working day
   *  `unknown`    no measurement and no learned window
   *
   *  It cannot change the ORDER of the cages — feeding raises every cage at
   *  the farm together — and is carried so that it can, once feeding is known
   *  per cage. */
  feeding: {
    feeding: boolean
    provenance: 'measured' | 'uncertain' | 'expected' | 'unknown'
    /** Why it is uncertain, as a code rather than a sentence — the service is
     *  not Norwegian and this screen is. Words live in `i18n.ts`. */
    why: 'masked_by_vessel' | 'unresolved_band' | null
  }
}

export interface FlowHour {
  time: string
  /** Null when the forecast has no current for this hour. The hour is still
   *  here: a gap is a fact about the forecast, and a scrubber that silently
   *  skips it tells the reader the hour does not exist. */
  current: {
    speed_ms: number
    bearing_toward: number
    slack: boolean
    exchange: 'slack' | 'weak' | 'moderate' | 'good' | 'strong' | null
  } | null
  sea_temperature_c?: number | null
  water_level_m?: number | null
  pens: FlowMark[]
  reason: string | null
  /** Absent when the farm has not bought the ranking, or when nobody has
   *  recorded any fish. Absent is not the same as an empty ranking. */
  oxygen?: FlowOxygen
}

/** What the upstream says it can answer here. `salinity` is false everywhere on
 *  this coast today, which is why the oxygen ranking is a ranking. */
export interface FlowCoverage {
  [metric: string]: {
    available: boolean
    tier?: string
    source?: string
    model?: string
    reason?: string
  }
}

export interface SiteFlow {
  schema: 1
  site_id: string
  /** False is an ordinary answer with four different causes, and `reason`
   *  says which: not sold, not configured, unreachable, or a model that does
   *  not cover this point. The map renders the sentence, never a blank. */
  available: boolean
  stale: boolean
  cache_age_seconds: number | null
  source: string
  reason: string | null
  coverage: FlowCoverage
  /** Never empty when `available`. A score is a rule somebody chose, and a
   *  rule that travels separately from its number becomes a fact on the way. */
  assumptions: string[]
  hours: FlowHour[]
  /** High and low water. Carried because the tide is WHY the arrows turn, and
   *  an explanation beside the thing it explains is the difference between a
   *  forecast somebody trusts and one they merely read. */
  tide_extremes?: { time: string; height: number; type: 'low' | 'high' }[]
  generated_at?: string | null
  /** Rings nobody has placed on the map. Named rather than dropped, so the
   *  page can say which cage it could not draw an arrow for. */
  unplaced: string[]
}

/** The path, not the fetch: this one is read through `useApi`, which needs a
 *  key it can cache and poll on rather than a promise. */
export function siteFlowPath(siteId: string, hours = 48) {
  return `/v1/sites/${encodeURIComponent(siteId)}/flow?hours=${hours}`
}

/* ---------------------------------------------------------------- laksvel */

/** Laksvel: standardisert operasjonell velferdsovervåking for laks i
 *  matfiskanlegg — Havforskningsinstituttet, rapport 2022-14, FHF 901554.
 *
 *  EVERY BAND BEHIND THESE NUMBERS IS THE PROTOCOL'S, and the scoring happens
 *  server-side for the reason `antallFor` gives about the lice ladder: what
 *  makes reading a value against a band a lookup rather than an opinion is
 *  that somebody else published the band, and a reading on this screen must
 *  not be able to land on a different ladder from the same reading elsewhere.
 *
 *  THERE IS NO MEAN SCORE IN THIS FILE, and there must never be one. §5.4 is
 *  explicit: the levels are not linear, so results must never be shown as
 *  averages or as changes in averages. Their own example — a hundred fish at
 *  score 1 becoming ninety at 1 and ten at 3 — moves the mean from 1.0 to 1.2,
 *  which reads as nothing while a tenth of the population acquires severe
 *  damage. Everything is a distribution: the share at each level, with the
 *  number of fish behind it. */
export interface LaksvelEnv {
  depth_m: number
  /** Percent of air saturation. Over 100 is real — a still bay in summer
   *  supersaturates. */
  o2_pct: number | null
  temp_c: number | null
  salinity_ppt: number | null
  /** Where the number came from. `modelled` is prefilled from the marine
   *  forecast and renders with `~`; it must never read as a probe reading. */
  source: 'manual' | 'modelled' | 'probe'
  /** 0 green, 1 yellow, 2 orange, 3 red. Null when the reading cannot be
   *  banded — oxygen without a temperature has no band at all, because every
   *  band is an offset from a line through it. */
  o2_score: number | null
  temp_score: number | null
}

export interface LaksvelPen {
  pen_id: string
  pen_no: string
  name: string | null
  depth_m: number | null
  env: LaksvelEnv[]
  /** Gruppebaserte indikatorer, scored 0–3 off the protocol's own tables. */
  atferd: number | null
  appetitt: number | null
  /** Fish found dead in this ring today. A count, not a score: the band
   *  depends on the population and on the window, so the server scores it. */
  n_dead: number | null
  note: string | null
  /** The cage's own scores. Temperature takes the BEST depth available (§3.2 —
   *  fish that can swim down have already solved a warm surface); oxygen takes
   *  the worst, because nothing can swim away from needing it. */
  temp_score: number | null
  o2_score: number | null
}

/** The ladder, sent with the data so the portal can paint a scale without
 *  owning the rule that draws it. A hardcoded copy in the browser is how a
 *  tapped value and a scored value end up disagreeing.
 *
 *  Oxygen gets the FORMULA and not a band table, because its edges are not
 *  constants: they move with the temperature, so a table handed over once
 *  would be wrong at every temperature but one. */
export interface LaksvelScale {
  temp: {
    min: number; max: number; step: number; unit: string
    bands: { from: number; to: number; score: number }[]
  }
  o2: {
    min: number; max: number; step: number; unit: string
    domaxfi: { slope: number; intercept: number
               yellow: number; green: number }
  }
  salinity: {
    min: number; max: number; step: number; unit: string
    /** Landmarks, not bands. §3.3 records salinity to understand which water
     *  masses are in the cage, and declines to score it. */
    marks: { at: number; label: string }[]
  }
}

export interface LaksvelDay {
  schema: 1
  site_id: string
  on: string
  pens: LaksvelPen[]
  depths: number[]
  scale: LaksvelScale
  /** The fourteen individual indicators, from the server, so the form is built
   *  from the protocol rather than from a list retyped into the browser. */
  indicators: { key: string; label: string }[]
}

export function laksvelPath(siteId: string, on?: string) {
  return `/v1/sites/${encodeURIComponent(siteId)}/laksvel`
    + (on ? `?on=${encodeURIComponent(on)}` : '')
}

/** Register one ring's day. One ring per request, like `addPenCount`, so a
 *  grid reports partial success honestly instead of losing seven good rows to
 *  one bad one. The whole day comes back. */
export function saveLaksvel(siteId: string, body: {
  pen_id: string
  on_date: string
  env?: { depth_m: number; o2_pct?: number | null; temp_c?: number | null
          salinity_ppt?: number | null; source?: string }[]
  atferd?: number | null
  appetitt?: number | null
  n_dead?: number | null
  note?: string | null
}) {
  return api.post<LaksvelDay>(
    `/v1/sites/${encodeURIComponent(siteId)}/laksvel`, body)
}

/** The whole farm in one request, because a map editor produces a drawing and
 *  not a stream of edits.
 *
 *  A live ring this does not mention is RETIRED -- not deleted: its counts
 *  survive and the week it was reported in still names it. That follows from
 *  the request being a statement about the whole farm, and it is why the editor
 *  has a save button rather than saving on every drag.
 */
export function saveSiteLayout(siteId: string, body: {
  pens: {
    pen_id?: string
    pen_no: string
    name?: string | null
    sort?: number
    circumference_m?: number | null
    depth_m?: number | null
    lat?: number | null
    lon?: number | null
    shape?: PenShape
    bearing_deg?: number | null
  }[]
  structures: {
    structure_id?: string
    kind?: string
    name?: string | null
    lat: number
    lon: number
    length_m?: number | null
    width_m?: number | null
    bearing_deg?: number | null
  }[]
}) {
  return api.put<SiteLayout>(
    `/v1/sites/${encodeURIComponent(siteId)}/layout`, body)
}

/** One ring on one day, inside the history matrix. */
export interface HistoryCell {
  adult_female: number | null
  mobile: number | null
  sessile: number | null
  n_fish: number | null
  not_counted_reason: string | null
  basis: string
}

export interface HistoryDay {
  counted_at: string
  /** The figure that was reported for the farm. */
  adult_female: number | null
  mobile: number | null
  sessile: number | null
  n_fish: number | null
  /** Which layer this day came from, and the seam between them is never
   *  smoothed:
   *
   *  - `register` — the regulator's own weekly figure for the locality,
   *    published weeks behind, going back as far as the register can answer.
   *    It has no merd axis and never will, so `cells` is empty.
   *  - `site` — somebody at the farm typed one figure for the whole farm.
   *    `cells` is empty here too, and the page spreads it.
   *  - `pens` / `fish` — the rings were counted, and `cells` has them. */
  basis: string
  pens_counted: number | null
  /** A delousing on record that week, from EITHER source. The register
   *  publishes it weeks late; the farm knows the day it happened. Unioned
   *  rather than ranked, because unlike a figure the two cannot disagree —
   *  a boat either came or it did not, and nobody's count contradicts it. */
  treated?: boolean
  /** Rings that held no fish that week. A blank cell has meant "nobody
   *  counted" until now, and an empty ring is a different thing entirely —
   *  it is what explains a column going quiet without anybody having failed
   *  to do their job. Empty for a farm that has not modelled its fish. */
  empty_pens?: string[]
  cells: Record<string, HistoryCell>
}

export interface PenHistory {
  /** Live rings first, then any ring that only exists in the past. A column
   *  that vanishes when a ring is retired takes its history with it. */
  pens: { pen_id: string; pen_no: string; name: string | null
          retired: boolean }[]
  days: HistoryDay[]
}

/** Every count this farm has, with the rings underneath each one.
 *
 *  One week of merd rows says which ring is worst today. A year of them says
 *  whether it is the same ring every time — and that is a claim about geography
 *  no single week can make and no site-level series can make at all.
 */
export function penHistory(siteId: string, limit = 26) {
  return api.get<PenHistory>(
    `/v1/sites/${encodeURIComponent(siteId)}/pen-history?limit=${limit}`)
}

/* ------------------------------------------------------------------- fisken

   THE RING IS THE PLACE AND THE FISH ARE THE THING.

   On the staff editor you drag rings, because that is geometry and it is set up
   once. Here you drag a badge, because that is an operation that happens most
   weeks — and the ring never moves. The two objects on one map are the domain
   distinction made visible, and nobody has to be told which is which. */

export interface FishGroup {
  group_id: string
  /** What it is called on the walkway. "H25", "22G". */
  name: string
  stocked_on: string | null
  origin: string | null
  n_fish: number | null
  mean_weight_g: number | null
  /** 'farm' outranks 'register'. A farmer saying so beats anything derived
   *  from vessel movement, and an audit must never confuse the two. */
  source: string
  split_from_group_id: string | null
  merged_into_group_id: string | null
  /** The day its last ring emptied to the slaughterhouse. Dated, not retired:
   *  its counts were filed and "this farm was empty from November" is a fact
   *  the forecast needs. */
  harvested_on: string | null
}

export interface Occupancy {
  occupancy_id: string
  pen_id: string
  group_id: string
  group_name: string
  started_on: string
  ended_on: string | null
  n_fish: number | null
  reason: string | null
  /** THE NEWEST COUNT OF THESE FISH, wherever it was taken.
   *
   *  The lice belong to the population, not to the ring. Move merd 6's fish
   *  into merd 3 and merd 6 has no fish and therefore no lice, while merd 3
   *  holds whatever those fish were last counted at. Reading either figure off
   *  the ring states the opposite of both and looks completely normal.
   *
   *  `counted_in_pen` is where it was taken, which is not always where they
   *  are now — said out loud rather than quietly dropped. */
  lice: {
    adult_female: number
    mobile: number | null
    sessile: number | null
    counted_at: string
    counted_in_pen: string
  } | null
}

export interface FishMove {
  occupancy_id: string
  on: string
  pen_id: string
  /** Null for a stocking, which is an arrival with no departure. */
  from_pen_id: string | null
  group_id: string
  group_name: string
  reason: string | null
  n_fish: number | null
}

export interface FishState {
  on: string
  groups: FishGroup[]
  /** Who was standing where on `on` — not "now", because the week strip under
   *  the map scrubs back and the badges move with it. */
  occupancy: Occupancy[]
  moves: FishMove[]
  /** What the register already knows, offered as one sentence, and only while
   *  the farm has said nothing. Settefisk from Strømsnes, carried by MOWI
   *  STAR, 7 July 2025 — all of it free, none of it typed. */
  suggestion: {
    segment_id: number | null
    name: string
    stocked_on: string | null
    origin: string | null
    origin_kind: string | null
    vessel: string | null
  } | null
}

export interface Hatchery {
  locality_no: number | null
  name: string
  operator: string | null
  /** This farm has taken smolt from here before. Those come first — a farm has
   *  used two or three of the register's 302, and alphabetical order would put
   *  the right answer on page four. */
  used_here: boolean
}

export function hatcheries(siteId: string, q?: string) {
  return api.get<{ items: Hatchery[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/hatcheries`
    + (q ? `?q=${encodeURIComponent(q)}` : ''))
}

/** Fish leaving the farm. The window closes and nothing opens — the one case
 *  where fish standing nowhere is a permanent, correct answer. */
/** One day a boat was actually at this farm, offered as a slaughter date.
 *
 *  `unexplained` is the ranking signal, not decoration: FDIR publishes
 *  delousings and the cohort publishes stockings, so a long call belonging to
 *  neither is a boat that came for something the register cannot name. */
export interface HarvestDate {
  on: string
  event_type: string
  method: string | null
  vessel_name: string | null
  mmsi: number | null
  dwell_min: number | null
  confirmed: boolean
  /** Why this day is on the list.
   *  `harvest`     — AIS watched a boat lie here and then go to a slaughterhouse.
   *  `unexplained` — a long call belonging to neither FDIR nor the cohort.
   *  `known`       — a day we can already account for, offered last. */
  grunn: 'harvest' | 'unexplained' | 'known'
  unexplained: boolean
}

/** `available: false` where the farm has not bought the vessel product, or
 *  where the analytics tables are mid-rebuild. The sheet falls back to its
 *  plain date field either way — this is never an error to show. */
export function harvestDates(siteId: string,
                             groupId?: string | null,
                             penId?: string | null) {
  /* The group and the ring are not optional in practice. Without them the
     answer is every wellboat call of the cycle, including the ones from
     before these fish were in this ring — which the harvest itself then
     refuses, one at a time. */
  const q = new URLSearchParams()
  if (groupId) q.set('group_id', groupId)
  if (penId) q.set('pen_id', penId)
  const s = q.toString()
  return api.get<{ schema: 1; available: boolean; candidates: HarvestDate[]
                   from: string | null; to: string }>(
    `/v1/sites/${encodeURIComponent(siteId)}/fish/harvest-dates${s ? `?${s}` : ''}`)
}

export function harvestFish(siteId: string, body: {
  group_id: string
  from_pen_id: string
  on: string
  note?: string | null
  /** The boat, when one was chosen — or typed, for a call AIS never saw. */
  vessel_name?: string | null
  mmsi?: number | null
}) {
  return api.post<{ emptied: boolean; groups: FishGroup[]
                    occupancy: Occupancy[]; moves: FishMove[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/fish/harvest`, body)
}

/** A delousing, with the rings it actually reached named.
 *
 *  §9 says a farm over the limit treats all production units "med mindre det
 *  kan dokumenteres at behandling av alle produksjonsenhetene er åpenbart
 *  unødvendig". That is a claim about WHICH rings, and this is the list. */
export function addPenTreatment(siteId: string, body: {
  pen_ids: string[]
  treated_at: string
  method: string
}) {
  return api.post<{ treatment_id: string; pens_treated: number
                    pens_total: number }>(
    `/v1/sites/${encodeURIComponent(siteId)}/pen-treatments`, body)
}

export function fishState(siteId: string, on?: string) {
  return api.get<FishState>(
    `/v1/sites/${encodeURIComponent(siteId)}/fish`
    + (on ? `?on=${encodeURIComponent(on)}` : ''))
}

export function createFishGroup(siteId: string, body: {
  name: string
  stocked_on?: string | null
  origin?: string | null
  register_segment_id?: number | null
  n_fish?: number | null
  pen_ids: string[]
  source?: string
}) {
  return api.post<{ group_id: string; groups: FishGroup[]
                    occupancy: Occupancy[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/fish/groups`, body)
}

/** One verb for three acts, decided by what is already in the target ring:
 *  a move, a split (`part: 'some'`), or a merge (the ring is occupied). */
export function moveFish(siteId: string, body: {
  group_id: string
  /** Onto an occupied ring: false merges the two populations, true pops the
   *  occupant out to stand nowhere until somebody says where it went. Two
   *  different Tuesdays, and only the person who was there knows which. */
  displace?: boolean
  /** Which ring they leave. Keying a move on the group alone empties every
   *  ring that group stands in — which at a farm running one generation is the
   *  whole array. The gesture is per ring; so is this.
   *
   *  Omitted only when placing fish that are standing NOWHERE, which is what a
   *  popped-out badge is. */
  from_pen_id?: string
  to_pen_id: string
  on: string
  part: 'all' | 'some'
  n_fish?: number | null
  reason?: string | null
  note?: string | null
}) {
  return api.post<{ occupancy_id: string
                    /** Non-null means a badge is now standing nowhere and the
                     *  page must ask where it goes. */
                    displaced: { group_id: string; group_name: string } | null
                    groups: FishGroup[]
                    occupancy: Occupancy[]; moves: FishMove[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/fish/moves`, body)
}

export function withdrawMove(siteId: string, occupancyId: string) {
  return api.del<{ groups: FishGroup[]; occupancy: Occupancy[]
                   moves: FishMove[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/fish/moves/`
    + `${encodeURIComponent(occupancyId)}`)
}

export function listPens(siteId: string) {
  return api.get<{ items: Pen[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/pens`)
}

export function penCounts(siteId: string, countedAt: string) {
  return api.get<{ counted_at: string; items: PenCount[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/pen-counts`
    + `?counted_at=${encodeURIComponent(countedAt)}`)
}

/** Record what was found at one ring, and get the farm's day back rolled up.
 *
 *  `fish` and the averages are alternatives, never both: sending both is
 *  sending a claim and its own contradiction, and the receiver refuses rather
 *  than picking one.
 */
export function addPenCount(siteId: string, body: {
  pen_id: string
  counted_at: string
  adult_female?: number | null
  mobile?: number | null
  sessile?: number | null
  n_fish?: number | null
  temp_c?: number | null
  note?: string
  not_counted_reason?: string
  fish?: { fish_no: number; adult_female?: number; mobile?: number
           sessile?: number; weight_g?: number }[]
}) {
  return api.post<{ count_id: string; pen_count_id: string
                    items: PenCount[]; site: PenRollup }>(
    `/v1/sites/${encodeURIComponent(siteId)}/pen-counts`, body)
}

export function withdrawPenCount(siteId: string, penCountId: string) {
  return api.del<{ items: PenCount[]; site: PenRollup }>(
    `/v1/sites/${encodeURIComponent(siteId)}/pen-counts/`
    + `${encodeURIComponent(penCountId)}`)
}

/** Decided server-side, in app/calibration.py, and never recomputed here.
 *  That is the point: if the browser assembled the three R2.3 terms itself,
 *  any bug in that assembly would invent an absolute level, and an invented
 *  level is indistinguishable from a real one once it is on a chart. */
export interface Calibration {
  absolute: boolean
  basis: string
  /** `SPL = dBFS + offset_db`, already summed by the server. Null unless every
   *  term is present, so it is safe to render whenever it is non-null. */
  offset_db?: number | null
  /** The sensitivity in play is the manufacturer's nominal figure for the
   *  model rather than a measurement of this serial number. Still absolute and
   *  still comparable between sites — but not a laboratory calibration, and
   *  anything that quotes a level has to carry the distinction. */
  estimated: boolean
  missing: string[]
  comparable_across_sites: boolean
  terms: {
    full_scale_dbv: number | null
    analogue_gain_db: number | null
    sensitivity_dbv_upa: number | null
  }
  /** Which terms are a datasheet figure rather than a measurement. Empty when
   *  the level is not absolute at all. The basis string already says *that*
   *  one of them is; this says which, for a screen that wants to say what to
   *  do about it. */
  estimated_terms?: string[]
  /** How wrong the absolute level could systematically be, in dB, one sigma.
   *  0 when the calibration is a real measurement, null when there is no
   *  absolute level at all. A level printed without this reads as a
   *  measurement, and on this fleet it is not one. */
  systematic_uncertainty_db?: number | null
  microphone_id: string | null
  sensitivity_source: {
    certificate_ref: string | null
    method: string | null
    valid_from: string
    estimated: boolean
  } | null
}

export interface FleetNode {
  compute_id: string
  display_name: string | null
  model: string | null
  full_scale_dbv: number | null
  full_scale_estimated?: boolean
  sw_version: string | null
  enrolled_at: string
  revoked_at: string | null
  mission_id: string | null
  site_id: string | null
  site_name: string | null
  mission_state: string | null
  mission_started_at: string | null
  tier: string | null
  desired_revision: number | null
  services: Record<string, string>
  last_upload_at: string | null
  /** Newest moment the node demonstrably spoke to us: an upload presign or a
   *  card inventory, whichever is later. Uploads alone made this track "is
   *  someone listening live" rather than "is this node alive", because an
   *  events-tier node uploads objects roughly hourly. */
  last_seen_at: string | null
  /** The hydrophone in the water on this unit, through its active mission.
   *  Not rendered on the fleet card -- "which sensor is this" is a question
   *  asked while planning a swap, not while glancing at what is broken -- but
   *  kept here because the card is not the only consumer. */
  microphone_id: string | null
  microphone_type: string | null
  microphone_serial: string | null
  hw_serial: string | null
  calibration: Calibration
  /** The deployment this unit is going to, when one has been arranged.
   *  Absent rather than null-filled, so the test is "is there a move" and not
   *  "are three fields set". */
  pending?: {
    mission_id: string
    activate_after: string | null
    site_name: string | null
  }
}

export interface Mission {
  mission_id: string
  site_id: string | null
  site_name?: string | null
  compute_id: string | null
  compute_name?: string | null
  microphone_id: string | null
  state: string
  started_at: string
  ended_at: string | null
  deploy_depth_m: number | null
  analogue_gain_db: number | null
  latitude: number | null
  longitude: number | null
  project_id: string | null
  tier: string | null
  revision: number
  calibration?: Calibration
  gain_setting?: string | null
  notes?: string | null
  microphone_label?: string | null
  microphone_serial?: string | null
  /** Non-null means the unit records without a compute: no uplink, no live
   *  stream, no services, and its card is imported by hand after recovery. It
   *  is also the key that will select the filename/header parser at import. */
  recorder_model?: string | null
  /** 'test' means excluded from everything Ocean impact claims. */
  purpose?: 'deployment' | 'test'
  /** What this farm has bought, resolved from company- and site-level grants.
   *  Optional so a bundle can run against a receiver that predates it; read as
   *  `m.features ?? []` and treat unknown as ungranted. */
  features?: string[]
  /** Set while this deployment is waiting for its node to turn up, and kept
   *  afterwards. Read next to `started_at` it says what was arranged beside
   *  what happened — a move planned for 13:00 that opened at 14:07 should
   *  show that rather than quietly read as punctual. */
  activate_after?: string | null
}

/** One deployment as the site detail reports it.
 *
 *  A site can have several of these active at once -- two boxes at one place is
 *  a legal state, and the exclusion constraints on `missions` are per compute
 *  and per microphone, never per site. Anything showing a farm's audio has to
 *  cope with that rather than picking one and hoping. */
export interface SiteMission {
  mission_id: string
  compute_id: string | null
  microphone_id: string | null
  state: string
  started_at: string
  ended_at: string | null
  deploy_depth_m: number | null
  tier: string | null
  /** Null on every unit in the fleet today, so the fallback to `type` is the
   *  path that runs rather than a defensive nicety. */
  microphone_label: string | null
  microphone_type: string | null
  microphone_serial: string | null
  /** Whether a level from this deployment may be called a decibel in the sea.
   *  Decided on the server (R5.3); the player's only job is to print it. */
  calibration?: Calibration
  /** What is painted on the enclosure -- `p-001`. The only name a person
   *  standing at the mooring with a phone can check against something
   *  physical; `cmp-5e8d9f7b` and `msn-2026-ljsy-n-14435-2` cannot be. */
  node_label: string | null
  /** The later of the newest presign on this mission and the node's last
   *  inventory post. What a liveness dot is drawn from, and an inference rather
   *  than presence -- see the fleet listing's docstring. */
  last_seen_at: string | null
}

export interface SiteDetail {
  schema: number
  site_id: string
  name: string
  municipality: string | null
  locality_no: number | null
  lat: number | null
  lon: number | null
  missions: SiteMission[]
  /** Feature keys this farm has bought — the union of its company-level and
   *  site-level grants, resolved server-side. Present so a screen can hide a
   *  panel the API would refuse anyway; the refusal is still the boundary, and
   *  hiding a link is a courtesy rather than a check. */
  features?: string[]
}

/** What POST /v1/commission/recorder takes.
 *
 *  Not a variant of the node commissioning body. There is no claim code —
 *  nothing on a recorder can redeem one — and the deployment window is real
 *  rather than starting the moment someone fills in the form.
 */
export interface RecorderIn {
  /** Optional when a locality number is given: the receiver reads the name
   *  from the register rather than trusting one sent alongside the number. */
  site?: string
  site_id?: string
  locality_no?: number
  serial: string
  recorder_model: string
  label?: string
  hydrophone_type?: string
  sensitivity_dbv_upa?: number
  calibration_method?: string
  certificate_ref?: string
  max_depth_m?: number
  depth_m?: number
  gain_setting?: string
  notes?: string
  started_at: string
  ended_at?: string
  state?: string
  /** Where the unit actually went, which lands on the mission. Distinct from
   *  the site's position: with a locality_no the register supplies that, and
   *  sending a refined pin here no longer overwrites it. */
  lat?: number
  lon?: number
  purpose?: 'deployment' | 'test'
}

export interface RecorderOut {
  mission_id: string
  site_id: string
  site_created: boolean
  microphone_id: string
  recorder_created: boolean
  recorder_model: string
  calibrated: boolean
  next?: string
}

export function commissionRecorder(body: RecorderIn) {
  return api.post<RecorderOut>('/v1/commission/recorder', body)
}

/** What POST /v1/commission takes — a node, which ends in a claim code. */
export interface CommissionIn {
  site?: string
  site_id?: string
  locality_no?: number
  hydrophone_serial: string
  hydrophone_type?: string
  sensitivity_dbv_upa?: number
  certificate_ref?: string
  depth_m?: number
  lat?: number
  lon?: number
  tier?: string
  purpose?: 'deployment' | 'test'
}

export interface CommissionOut {
  claim_code: string
  expires_in_days: number
  mission_id: string
  site_id: string
  microphone_id: string
  next?: string
}

export function commissionNode(body: CommissionIn) {
  return api.post<CommissionOut>('/v1/commission', body)
}

/** One farm from the aquaculture register, proxied and cached by the receiver.
 *
 *  Not fetched from api.phoniq.no directly: the dashboard's CSP allows
 *  `connect-src 'self'` only, and that API returns no
 *  Access-Control-Allow-Origin on the actual GET even though it answers the
 *  preflight — so a browser would discard a 200 after a preflight that looked
 *  like it succeeded. See app/localities.py.
 */
export interface Locality {
  locality_no: number
  name: string
  municipality: string | null
  municipality_no: string | null
  lat: number
  lon: number
  kind: string | null
}

export interface LocalityPage {
  total: number
  items: Locality[]
  source: string
  cache_age_seconds: number | null
  /** The register was unreachable and this list is the last good one. Say so
   *  rather than letting an old list look current. */
  stale: boolean
  error: string | null
}

export function searchLocalities(q: string, limit = 20) {
  return api.get<LocalityPage>(`/v1/localities${qs({ q, limit })}`)
}

/* ------------------------------------------------- customers and products */

export interface Org {
  org_id: string
  name: string
  kind: 'internal' | 'customer'
  register_slug: string | null
  org_no: string | null
  disabled_at: string | null
  site_count: number
  member_count: number
  area_count: number
}

export interface OrgList {
  items: Org[]
  /** Free-text operator names on sites that have no owner yet. The
   *  reconciliation worklist: until it is empty there are farms belonging to
   *  nobody, and a farm belonging to nobody is invisible to the customer
   *  paying for it. */
  unassigned_operators: string[]
  sites_without_owner: number
}

export interface Area {
  area_id: string
  name: string
  parent_area_id: string | null
  site_count: number
}

export interface OrgSite {
  site_id: string
  name: string
  area_id: string | null
  locality_no: number | null
  municipality: string | null
}

export interface OrgDetail {
  org_id: string
  name: string
  kind: string
  register_slug: string | null
  org_no: string | null
  disabled_at: string | null
  areas: Area[]
  sites: OrgSite[]
}

export interface Person {
  user_id: string
  email: string
  display_name: string | null
  member_role: MemberRole
  expires_at: string | null
  created_at: string
  last_login_at: string | null
  disabled_at: string | null
  /** The account has no password: they were invited and have not been in yet.
   *  The first thing anyone wants to know when a customer says nobody can log
   *  in. */
  awaiting_setup: boolean
  invite_pending: boolean
  area_ids: string[]
  site_ids: string[]
}

export interface ProductFeature {
  feature_key: string
  name: string
  sort: number
}

export interface Product {
  product_id: string
  name: string
  summary: string | null
  sort: number
  retired_at: string | null
  features: ProductFeature[]
}

export interface Entitlement {
  entitlement_id: string
  product_id: string
  product_name: string
  org_id: string | null
  site_id: string | null
  org_name: string | null
  site_name: string | null
  valid_from: string
  valid_to: string | null
  revoked_at: string | null
  note: string | null
  features: string[]
}

/** A company in Akvakulturregisteret, as the picker offers it. */
export interface RegisterCompany {
  slug: string
  name: string
  org_nr: string | null
  county: string | null
  municipality: string | null
  n_sites: number | null
}

/** Search the register. Accent-folded on the receiver, because the register's
 *  own search is not: `lerøy` finds ten companies there and `leroy` finds
 *  none, and nobody typing into a picker reliably reaches for ø. */
export function searchRegister(q: string, limit = 8) {
  return api.get<{ items: RegisterCompany[]; total: number; stale: boolean }>(
    `/v1/operators${qs({ q, limit })}`)
}

/** See the portal as this customer's company manager sees it, for 30 minutes.
 *  Read-only, audited, and it never borrows a customer's identity -- the audit
 *  trail keeps naming the staff member. */
export function startViewAs(orgId: string) {
  return api.post<{ org_name: string; until: string }>(
    '/v1/auth/view-as', { org_id: orgId })
}

export function stopViewAs() {
  return api.post('/v1/auth/view-as/stop')
}

export function listOrgs() {
  return api.get<OrgList>('/v1/orgs')
}

export function createOrg(body: {
  name: string; register_slug?: string; org_no?: string
}) {
  return api.post<{ org_id: string }>('/v1/orgs', body)
}

export function getOrg(orgId: string) {
  return api.get<OrgDetail>(`/v1/orgs/${encodeURIComponent(orgId)}`)
}

export function createArea(body: {
  org_id: string; name: string; parent_area_id?: string | null
}) {
  return api.post<{ area_id: string }>('/v1/areas', body)
}

/** Removes an area only when nothing is in it -- no farms, no nested areas,
 *  nobody scoped to it. The receiver answers 409 naming what is left. */
export function deleteArea(areaId: string) {
  return api.del(`/v1/areas/${encodeURIComponent(areaId)}`)
}

/** Gives a company a farm from the locality register, created if it does not
 *  exist yet. An existing farm owned by another company is refused, not moved. */
export function addOrgSite(orgId: string,
                           body: { locality_no: number; area_id?: string | null }) {
  return api.post<{ site_id: string; name: string; created: boolean }>(
    `/v1/orgs/${encodeURIComponent(orgId)}/sites`, body)
}

export function listPeople(orgId: string) {
  return api.get<{ items: Person[] }>(`/v1/people${qs({ org_id: orgId })}`)
}

/** Creates an account with NO password and mails its owner a single-use link.
 *  Nobody ever chooses a password for somebody else. */
export function invitePerson(body: {
  email: string
  display_name?: string
  org_id: string
  member_role: MemberRole
  expires_at?: string | null
  area_ids?: string[]
  site_ids?: string[]
  lang?: 'nb' | 'en'
}) {
  return api.post<{ user_id: string; invited: boolean; expires_at: string | null }>(
    '/v1/invites', body)
}

export function resendInvite(orgId: string, userId: string) {
  return api.post('/v1/invites/resend', { org_id: orgId, user_id: userId })
}

export function revokeMembership(orgId: string, userId: string) {
  return api.post('/v1/people/revoke', { org_id: orgId, user_id: userId })
}

export function listProducts() {
  return api.get<{ items: Product[] }>('/v1/products')
}

export function listEntitlements(params: { org_id?: string; site_id?: string }) {
  return api.get<{ items: Entitlement[] }>(`/v1/entitlements${qs(params)}`)
}

export function grantEntitlement(body: {
  product_id: string
  org_id?: string | null
  site_id?: string | null
  features?: string[]
  valid_to?: string | null
  note?: string
}) {
  return api.post<{ entitlement_id: string }>('/v1/entitlements', body)
}

export function revokeEntitlement(entitlementId: string) {
  return api.post(
    `/v1/entitlements/${encodeURIComponent(entitlementId)}/revoke`)
}

/** Assigning a site to a company is the single act that makes a customer able
 *  to see anything at all. */
export function setSiteOwner(siteId: string,
                             body: { org_id?: string | null; area_id?: string | null }) {
  return api.patch(`/v1/sites/${encodeURIComponent(siteId)}`, body)
}

/* ------------------------------------------------------------------- lice */

/** One count made at the farm. `counted_at` is the day the fish were looked at;
 *  `recorded_at` is when somebody typed it in. Keeping them apart is what stops
 *  a three-day-old number from looking fresh. */
export interface LiceCount {
  count_id: string
  counted_at: string
  adult_female: number | null
  mobile: number | null
  sessile: number | null
  n_fish: number | null
  /** Sea temperature at the cage when the fish were counted, °C. Temperature
   *  decides whether the chalimus on the fish today become adult females in two
   *  weeks or in four. The register carries it as well, on the same weekly lag
   *  as the count — which is what a reading taken this morning gets ahead of. */
  temp_c?: number | null
  note: string | null
  recorded_at: string | null
  /** A third time again, and a correction moves only this one. Null until
   *  somebody fixes the row. */
  corrected_at?: string | null
  counted_by: string | null
  /** Whether a person or a field device entered it. The name above is a label
   *  either way -- a device's is what somebody typed on it -- so this is what
   *  distinguishes "Merd 3 the tablet" from "Merd 3 the person", which a
   *  register somebody may be audited against has to be able to do. */
  counted_by_kind?: 'person' | 'device' | null
  /** How the forecast landed against this count. Null when the count fell
   *  outside the four weeks the forecast covered, and on every count entered
   *  before the receiver started keeping these. */
  check?: LiceCheck | null
}

/** What the forecast said, frozen the moment before the count that tests it.
 *
 *  Read on the server before the count is written, so the model that made the
 *  claim had never seen the number now being compared to it. That is what makes
 *  this an out-of-sample test rather than a story told afterwards. */
export interface LiceCheck {
  central: number
  p10: number | null
  p90: number | null
  /** `voided` means the farm deloused between the forecast's origin and the
   *  count. The forecast's own sentence is "om dere ikke avluser før det", so it
   *  described a world that did not happen — NOT a miss, and never counted as
   *  one. */
  verdict: 'inside' | 'above' | 'below' | 'voided' | null
  weeks_ahead: number
  origin_date: string | null
  origin_kind: 'register' | 'telling' | null
  origin_value: number | null
  grense: number | null
  target_week: string | null
  symbol: Symbolkode | null
  /** Which head made this claim. v2's centre was the median and v2b's is the
   *  mean, so two checks from either side of 2026-08-18 are not the same
   *  quantity — never average a column of these, and say which one a row is. */
  model?: string | null
}

/** The model's own five keys, whose per-stage knockdown they have measured. */
export type Metode =
  | 'kaldtvann' | 'ferskvann' | 'medikament' | 'mekanisk' | 'termisk'

export interface LiceTreatment {
  treatment_id: string
  treated_at: string
  method: Metode
  coverage: number
  /** A farmer saying "we deloused on Tuesday" outranks a suspicion derived from
   *  a wellboat's movements, and the two must not be confused. */
  source: 'farm' | 'register' | 'suspected'
  recorded_at: string | null
  recorded_by: string | null
}

/** Which figure is operative right now, and why. The farm's own count wins
 *  whenever it is newer than the register's reporting window — the register
 *  publishes a monthly window weeks behind, which is the right number for a
 *  regulator and a useless one for deciding whether to treat on Thursday. */
export interface LiceCurrent {
  source: 'own_count' | 'register'
  as_of: string | null
  adult_female: number | null
  counted_by: string | null
  recorded_at: string | null
  threshold: number | null
  why: string
  status: 'over' | 'approaching' | 'under' | 'unknown'
  note?: string
}

/** The model's answer. Absent when the model is not wired up, in which case the
 *  screen shows the register comparison and calls nothing a forecast. */
/** One week of the forecast, on the REPORTED scale — latent burden plus
 *  counting noise — because that is the scale the regulation acts on. */
export interface LiceWeek {
  uke: number
  dato: string
  grense: number
  p10: number
  p25: number
  p50: number
  p75: number
  p90: number
  p99: number
  /** Probability of breaching in THIS week. */
  pUke: number
  /** Cumulative probability of having breached by this week. */
  pKum: number
}

export interface LiceForecast {
  fitId?: number
  /** The ISO week Monday the forecast is anchored on. A count for a later week
   *  moves it, so a four-week horizon stays four weeks. */
  somAv?: string
  tilstand?: {
    fastsittende: number
    bevegelige: number
    voksne: number
    voksneP10: number
    voksneP90: number
  }
  basis?: {
    /** Cumulative breach probability over the WHOLE horizon, which runs to
     *  harvest and is usually longer than four weeks. Not the four-week
     *  number: take that from `uker` at `ledetid`. */
    risiko: number
    uker?: LiceWeek[]
  }
  /** Varies through the year, so an object rather than a number. Each week in
   *  `uker` carries the limit in force that week. */
  grense?: { lav: number; hoy: number; varierer: boolean }
  /** Weeks of lead time the decision needs: order the boat at four, decide at
   *  two. Also the index into `uker` for the four-week answer. */
  ledetid?: number
  horisont?: { bedt: number; uker: number; tilSlakt: boolean }
  datakvalitet?: Record<string, unknown>
  sone?: { navn: string | null; farge: string | null }
  available?: false
  stale?: boolean
  error?: string | null
  cache_age_seconds?: number
}

export interface LiceRow {
  site_id: string
  name: string
  locality_no: number | null
  avg_lice: number | null
  action_threshold: number | null
  current: LiceCurrent
  own_count_total: number
}

/* ------- v2: four weeks, a closed symbol set, probabilities that verify ---- */

export type Symbolkode =
  | 'lavt_stabilt' | 'lavt_stiger' | 'lavt_vokser_raskt'
  | 'middels_synker' | 'middels_stabilt' | 'middels_stiger'
  | 'over_grensen' | 'over_synker'
export type Pil = 'ned' | 'flat' | 'opp' | 'opp_bratt'

/** The three thresholds the model scores itself against, spelled the way the
 *  payload spells them: `0.5` becomes `0_5`. The limit in force that week is
 *  one OF these, which is why `pOverGrense` has no `Brukbar` twin of its own —
 *  its verdict is the verdict of whichever threshold `grense` equals. */
export type Grensekode = 'pOver0_1' | 'pOver0_2' | 'pOver0_5'

/** `0.5` → `pOver0_5`. Formatted rather than looked up so a limit we have not
 *  seen before fails to match loudly instead of resolving to the wrong cell. */
export function grensekode(grense: number): Grensekode {
  return `pOver${String(grense).replace('.', '_')}` as Grensekode
}

/** Whether one probability at one horizon is worth serving, and on what.
 *
 *  `bssHoldout`/`eceHoldout` are null under v2b and that is honest rather than
 *  broken: the holdout parquet holds v2's predictions, so scoring v2b against
 *  it would be a verdict that looks measured and is about a different model.
 *  Read `brukbar` and `bssUtvikling`; never treat a null holdout score as a
 *  failed one. */
export interface Kvalitet {
  brukbar: boolean | null
  grunn: string | null
  skiller: number | null
  kalibrert: boolean | null
  bssHoldout: number | null
  eceHoldout: number | null
  bssUtvikling: number | null
}

export interface VarselUke {
  time: string
  data: {
    instant: {
      details: {
        /** THE HEADLINE, and which centre it is depends on the head. Under v2b
         *  it is the MEAN — the unbiased answer to "what will the count say" —
         *  and under v2 it was the median. That is the fix for "the forecast is
         *  always lower than we measure", and it reads as a level rise of +8 %
         *  at one week to +22 % at eight, fleet-wide. Nothing here changed
         *  name or meaning; the number moved because the question it answers
         *  was corrected.
         *
         *  Do NOT infer the head from whether `median` differs from this. Read
         *  `meta.modell`. */
        voksneHunnlus: number
        /** The 50/50 line, and the number the SYMBOL is classified on. Present
         *  on both heads and absent only on the origin row. It sits beside the
         *  headline rather than replacing it because for a right-skewed
         *  distribution the two are different questions, and a reader must be
         *  able to tell which number the arrow agrees with. */
        median?: number
        p10?: number
        p90?: number
      }
    }
    /** Absent on the origin row, which is an observation rather than a
     *  forecast. */
    uken?: {
      summary: {
        symbol_code: Symbolkode
        tekst: string
        ikon: string
        /** How many glyphs to draw. Quantity by repetition, never by scaling:
         *  the eye reads count correctly and area badly. */
        ikonAntall: number
        pil: Pil
      }
      details: {
        /** Per week. 0.50 normally, 0.20 in weeks 16–21, 0.10 there in a red
         *  production area — and five weeks later than that north of
         *  Nord-Trøndelag. Reading [0].grense once and reusing it would be
         *  invisible now and wrong in April. */
        grense: number
        /** P(the count comes in at or over `grense`). Whether it may be SHOWN
         *  is `pOverGrenseBrukbar()`, not this field's presence — it is served
         *  whether or not it verifies. */
        pOverGrense: number
      } & Partial<Record<Grensekode, number>>
        & Partial<Record<`${Grensekode}Brukbar`, boolean | null>>
      /** Per threshold, keyed `pOver0_1` / `pOver0_2` / `pOver0_5`. */
      kvalitet?: Partial<Record<Grensekode, Kvalitet>>
      /** The evidence for this exact symbol at this exact horizon, so the cell
       *  can carry its own proof rather than borrowing the strip's. */
      verifisert?: Verifisert | null
    }
    /** The horizon in weeks — 1..4. Named in the cell so "963 cases" is
     *  attributable to a week rather than to the forecast in general. */
    h?: number
    kilde?: string
  }
}

export interface Varsel {
  meta: {
    /** WHICH HEAD ANSWERED — `v2b` today, `v2` before 2026-08-18. This is the
     *  discriminator the model publishes for exactly this purpose, and it is
     *  the only thing worth branching on. Branching on whether `median`
     *  differs from `voksneHunnlus`, or on whether a mean is present, is
     *  branching on a symptom of the payload's shape rather than on the fact.
     *  A rollback shows up here too. */
    modell: string
    versjon: string
    oppdatert: string
    lokalitet: number
    navn: string
    /** 'live' or a fallow site, whose forecast is dated years ago and must not
     *  be shown as today's. */
    syklus: string
    origo: {
      dato: string
      uke: number
      voksneHunnlus: number
      ukerSidenTelling: number
      /** Which of the two the forecast was anchored on. `telling` means the
       *  farm's own count moved the origin; `register` means it did not reach
       *  the model. The page states this rather than inferring it from a zero —
       *  a page that claims the count was used when it was not is worse than
       *  one that never mentions it. */
      kilde?: 'register' | 'telling'
    }
    /** ON `meta`, not at the top level. It reads as a sibling of `timeseries`
     *  in the docs and it is not one — this was declared at the top level for
     *  a day, which made every read of it silently `undefined` and the
     *  synthetic-weeks footnote a no-op that nobody could see failing.
     *
     *  `syntetiskeUker` counts weeks the model had to invent between the
     *  register's last row and the count; those carry no treatment record, so
     *  a delousing in the gap is invisible to it.
     *
     *  `temperaturBrukt`/`temperaturForkastet` say what became of the
     *  thermometer readings we sent: the model's band is 0.5–25 °C where the
     *  wire accepts −2–30, so a reading can be stored here and dropped there.
     *
     *  `temperaturVirkning` is their own sentence about what it changed — the
     *  origin week and nothing past it. They measured anomaly persistence on
     *  143 571 site-weeks: carrying one reading forward flat is worse than
     *  assuming normal from week three. */
    tellinger?: {
      antall?: number
      /** The denominator each count was actually computed at, echoed back in
       *  the order they were sent. Accepted since this endpoint shipped and
       *  only returned since v2b, so until now nobody could check what their
       *  own number was divided by. */
      fiskPerTelling?: number[]
      /** Whether the SITE's baseline denominator is measured or the fleet
       *  default of 100 — NOT a statement about the counts above. `n_fish` is
       *  never reported by anyone; it is reverse-engineered from the published
       *  mean, and this says when that failed and 100 was assumed. */
      nFiskAntatt?: boolean
      syntetiskeUker?: number
      temperaturBrukt?: number
      temperaturForkastet?: number
      temperaturVirkning?: string
    } | null
    horisont: { uker: number; maks: number; grunn: string }
    produksjonsomraade?: { id: number; farge: string }
  }
  timeseries: VarselUke[]
  forbehold?: string
  advarsler?: string[]
  available?: false
  stale?: boolean
  error?: string | null
}

/** Is this week's `pOverGrense` a number the page may print?
 *
 *  There is no `pOverGrenseBrukbar` on the wire, and that is not an oversight:
 *  `grense` IS one of the three scored thresholds, so the verdict on the
 *  probability of crossing it is the verdict on that threshold. Resolve
 *  `grense` to its key and read that.
 *
 *  This gate is the reason the figure was off the page under v2 — P(>= 0.5)
 *  had lost all skill past week one, and the model was serving a number it
 *  could not stand behind. Under v2b all twenty-four threshold-horizon cells
 *  come back usable, so the same gate now opens. It is read rather than
 *  assumed in either direction, which is what makes it survive the next
 *  retrain without anybody remembering this.
 *
 *  Undefined `brukbar` means the model declined to say. That is not a yes. */
export function pOverGrenseBrukbar(uke: VarselUke['data']['uken']): boolean {
  if (!uke) return false
  const k = grensekode(uke.details.grense)
  const flagg = uke.details[`${k}Brukbar` as const]
  if (flagg != null) return flagg
  return uke.kvalitet?.[k]?.brukbar === true
}

/** What was actually counted, out of fold: how often farms that got this exact
 *  symbol at this exact horizon stayed under their own limit. Every share is
 *  `null` below a hundred cases — then there is no figure, and the page must
 *  not pretend there is one. */
export interface Verifisert {
  n: number
  andelUnderGrense: number | null
  andelSteg: number | null
  andelFalt: number | null
  observertSnitt: number
}

/** The counted frequency per symbol, from the holdout year. This is the number
 *  the page shows — not the model's own P(over the limit), which is marked
 *  `brukbar: false` from week two.
 *
 *  Note that `/v2/symboler` spells it `ikon_antall` while `/v2/varsel` spells
 *  it `ikonAntall`. Same fact, two spellings; theirs, not ours to fix. */
export interface SymbolTable {
  regel?: string
  kilde?: string
  symboler: {
    symbol: Symbolkode
    tekst: string
    ikon_antall: number
    pil: Pil
    verifisert: Record<string, Verifisert>
  }[]
}

/* ------- v3: the theoretical arm, on its own scale and its own clock ------ */

/** One week of the stage-dynamics integration.
 *
 *  `sentral` is on the LATENT scale -- the burden itself, with no counting
 *  noise -- while v2's numbers are on the reported scale the regulation acts
 *  on. The two are not comparable as levels and this application never prints
 *  one beside the other. Carried because the crossing has to be found in it,
 *  not because it is a number to show. */
export interface V3Uke {
  h: number
  dato: string
  sentral: number
  p10?: number
  p90?: number
}

/** The first week the central path reaches the limit, linearly interpolated.
 *
 *  `uke` is a FRACTIONAL HORIZON INDEX, not an ISO week -- 0.08 means "eight
 *  hundredths of the way into the first week". `dato` is the unambiguous one
 *  and is what this application reads; see `Lunte.tsx` for why aligning the two
 *  models by horizon index would silently put them a week apart.
 *  Null when it does not reach the limit inside the horizon. */
export interface V3Kryssing {
  uke: number
  dato: string
  grense: number
  regel: string
}

export interface V3Forecast {
  modell?: string
  /** `teoretisk` today. This is what the model IS, and it is the word the page
   *  uses for it -- not "uverifisert", which names a defect rather than a
   *  purpose. A theoretical dynamics model is not a broken empirical one. */
  variant?: string
  versjon?: string
  /** v3 takes no counts, so its origin is wherever the register last was --
   *  which is NOT necessarily where v2's origin is, because v2 moves to the
   *  farm's own count. Read before trusting the two horizons to line up. */
  origo?: { dato: string; uke: number; voksneHunnlus: number }
  uker?: V3Uke[]
  kryssing?: V3Kryssing | null
  /** False today, and the marker is drawn hollow BECAUSE of this flag rather
   *  than because of which endpoint it came from -- so the day it is scored,
   *  the drawing corrects itself without anyone remembering to. */
  verifisert?: boolean
  forbehold?: string
  syklus?: string
  available?: false
  stale?: boolean
  error?: string | null
  cache_age_seconds?: number
}

/** One ISO week as the register published it — BarentsWatch and FDIR, arriving
 *  through the receiver rather than the browser.
 *
 *  `week` is the Monday. The register runs about two weeks behind the week it
 *  describes, so these fill the OLDEST rows of the panel and can never fill
 *  this one; the farm's own count is the only thing that can. Where the two
 *  cover the same week the count wins — it was taken at the cage, and this is a
 *  published average. */
export interface RegisterWeek {
  week: string
  adult_female: number
  /** A delousing on record that week. It is why a series drops to zero, and a
   *  reader comparing two weeks without it reads a successful treatment as a
   *  modelling failure. */
  treated: boolean
}

export interface LiceDetail {
  available: boolean
  reason?: string
  locality_no?: number
  register_name?: string
  avg_lice?: number | null
  max_lice?: number | null
  action_threshold?: number
  /** The register's own in-situ sea temperature, averaged over the report
   *  window. Used to seed the count form rather than to display: a month-old
   *  mean for this exact locality beats an empty field, and correcting a
   *  number is less work than producing one. */
  sea_temperature_mean_c?: number | null
  /** The register's weekly series, most recent last. Empty for a locality the
   *  weekly publication does not cover, which is not an error — the panel then
   *  shows the weeks it can and an em dash for the rest. */
  register_weeks?: RegisterWeek[]
  /** Whether the model served here can answer a delousing question at all.
   *  Asked of the model, not assumed: a process that predates the delousing
   *  work 404s the route, and offering a control that errors is worse than
   *  offering nothing. */
  avlusing_available?: boolean
  window_start?: string
  window_end?: string
  own_counts?: LiceCount[]
  /** Delousings on record. Sent with the panel so the count form can pre-fill
   *  "have you deloused" instead of asking what we already know. */
  treatments?: LiceTreatment[]
  current?: LiceCurrent
  varsel?: Varsel | null
  /** Sent only alongside `varsel`, and never folded into `current`. It is the
   *  second half of a picture whose first half is v2. */
  v3?: V3Forecast | null
  symbols?: SymbolTable | null
  forecast?: LiceForecast | null
  /** v3 is deliberately absent from this field. It is not a source of any
   *  number the page prints. */
  /** Which arm produced what the panel is showing. The receiver names the
   *  head it actually got rather than the API family it called, so this reads
   *  `v2b` today; treat any `v2…` value as "the measured four-week model". */
  forecast_source?: 'v2' | 'v2b' | 'model' | 'none'
  stale?: boolean
  cache_age_seconds?: number | null
}

export function listLice() {
  return api.get<{ items: LiceRow[]; stale: boolean }>('/v1/lice')
}

export function siteLice(siteId: string) {
  return api.get<LiceDetail>(`/v1/sites/${encodeURIComponent(siteId)}/lice`)
}

/** Recording a count re-runs the model against it, and the answer returned is
 *  the one that number produced.
 *
 *  `treatment` is asked for by the form because the forecast only ever claimed
 *  what would happen if nobody deloused. Declaring one voids the comparison
 *  rather than failing it. */
export function addLiceCount(siteId: string, body: {
  counted_at: string
  adult_female?: number | null
  mobile?: number | null
  sessile?: number | null
  n_fish?: number | null
  temp_c?: number | null
  note?: string
  treatment?: { treated_at: string; method: Metode; coverage: number } | null
}) {
  return api.post<{
    items: LiceCount[]; current: LiceCurrent; check: LiceCheck | null
  }>(`/v1/sites/${encodeURIComponent(siteId)}/lice-counts`, body)
}

/** Take a count back. The upsert corrects a NUMBER but never a DATE, so this is
 *  the only way to undo a count entered against the wrong day — and such a
 *  count anchors the model and wins the farm card indefinitely.
 *
 *  Stamped rather than deleted on the server: somebody did record it, and that
 *  is part of the process-control record even once it turns out to be wrong. */
export function withdrawLiceCount(siteId: string, countId: string) {
  return api.del<{ items: LiceCount[]; current: LiceCurrent }>(
    `/v1/sites/${encodeURIComponent(siteId)}/lice-counts/`
    + encodeURIComponent(countId))
}

export function listLiceCounts(siteId: string) {
  return api.get<{ items: LiceCount[]; treatments: LiceTreatment[] }>(
    `/v1/sites/${encodeURIComponent(siteId)}/lice-counts`)
}

/* ------------------------------------------ what a delousing would buy */

/** One fan, summarised. `snitt` is the centre the portal publishes — the same
 *  functional v2b publishes, which is what makes the two branches comparable.
 *  A difference between a mean and a median is not a difference. */
export interface AvlusingFan {
  snitt: number
  median: number
  q10: number
  q90: number
}

/** The knockdown week: the first week whose count reflects the boat. */
export interface AvlusingUke extends Record<string, unknown> {
  aar: number
  uke: number
  grense: number
  voksneHunnlus: AvlusingFan
  bevegelige: AvlusingFan
  fastsittende: AvlusingFan
  /** Quoted at all three regulatory levels regardless of which is in force, so
   *  a farmer can see 0,2 coming while 0,5 is still the rule. */
  sannsynlighetOver?: Record<string, number>
  sannsynlighetOverGrense?: number
}

/** One of the weeks after the boat. Carries `aar`/`uke` because THE JOIN TO THE
 *  DO-NOTHING FORECAST IS BY CALENDAR WEEK, never by position — the two answers
 *  do not share an origin offset, so a join by index slides one against the
 *  other without ever looking wrong. */
export interface AvlusingEtter extends AvlusingFan {
  ukerEtter: number
  aar: number
  uke: number
  grense: number
  /** SERVED, AND DELIBERATELY NOT RENDERED. Measured out-of-fold the chain
   *  cannot beat its own base rate from week 2 (BSS −0.23, −0.12, −0.14), and
   *  the oracle arm handed the true post-treatment count fails identically — so
   *  this is v2b's ceiling on a post-delousing trajectory, not a fixable error.
   *  A number that cannot beat its base rate must not be drawn as a percentage
   *  beside one that can. The fan carries the uncertainty out here instead. */
  sannsynlighetOverGrense?: number
}

export interface AvlusingSvar {
  schema: 1
  available: boolean
  reason?: string
  boat_week: { aar: number; uke: number }
  /** Derived by the receiver from the newest count's week, not typed by the
   *  farmer — and it moves the whole answer by a week, so the page states it. */
  counted_before_boat: boolean
  baatuke?: { aar: number; uke: number }
  origo?: {
    aar: number; uke: number
    ukerTilAvlusing: number
    ukerGammelt: number
    voksneHunnlus: number | null
    bevegelige: number | null
    fastsittende: number | null
  }
  /** Three versions, not one: the knockdown and the growth model are published
   *  from separate pointers on purpose, so a v2b refit can never silently move
   *  the knockdown, nor the reverse. */
  modell?: { knockdown: string; vekst: string; vekstHode: string }
  fartoy?: { mmsi: number | null; kjent: boolean; timer: number | null }
  advarsler?: string[]
  /** Integrator-facing prose, both of them — they name routes and field names.
   *  The page writes its own sentence for the farmer rather than passing these
   *  through; what they carry is the REASON, not the wording. */
  forbehold?: string
  kvalitet?: Record<string, string>
  generertKl?: string
  /** THE ANSWER IS CONDITIONAL ON THE DELOUSING WORKING — the knockdown is
   *  fitted on campaigns whose next count came in below the one before, so a
   *  pass that moved nothing is not in the training frame. The page must show
   *  this sentence rather than let the fan imply an unconditional promise. */
  forutsetning?: { avlusingaVirker?: string; utenAvlusing?: string }
  avlusing?: AvlusingUke
  etterpaa?: AvlusingEtter[]
}

/** What one planned delousing would buy at this site, for a boat in this ISO
 *  week. A calculator, not a record: nothing is stored, here or on the server.
 *
 *  The do-nothing branch is NOT in this answer. It is the `varsel` block of
 *  `getLice`, and the panel joins the two on absolute ISO week. One model owns
 *  each branch, so no number on the page has two sources that can disagree. */
export function avlusingFor(siteId: string, aar: number, uke: number,
                            mmsi?: number) {
  const q = new URLSearchParams({ aar: String(aar), uke: String(uke) })
  if (mmsi != null) q.set('mmsi', String(mmsi))
  return api.get<AvlusingSvar>(
    `/v1/sites/${encodeURIComponent(siteId)}/lice-avlusing?${q}`)
}

/* ------------------------------------------------------------- the account */

export interface Session {
  created_at: string
  last_seen_at: string
  expires_at: string
  user_agent: string | null
  ip: string | null
}

export function mySessions() {
  return api.get<{ items: Session[] }>('/v1/auth/sessions')
}

/** Ends every session but the one making the request, which is why the page it
 *  is pressed from keeps working. */
export function revokeOtherSessions() {
  return api.post('/v1/auth/sessions/revoke')
}

export function changePassword(current_password: string, new_password: string) {
  return api.post('/v1/auth/password', { current_password, new_password })
}

/** Starts the move. Nothing changes until the new address answers, and the
 *  reply is the same whether or not it is already someone else's. */
export function changeEmail(email: string) {
  return api.post('/v1/auth/email', { email })
}

export interface StoredObject {
  key: string
  kind: string
  ts: string
  size: number | null
  md5: string | null
  state: 'pending' | 'stored'
  attribution_conflict: boolean
  presigned_at: string
  stored_at: string | null
  start_utc: string | null
}

/** Where a mission's audio is, hour by hour, over an arbitrary range.
 *
 *  The server's answer to "can I still get 03:00 last Tuesday". Read alongside
 *  the levels chart, on the same axis.
 */
export interface Availability {
  schema: 1
  mission_id: string
  since: string
  until: string
  segment_seconds: number
  /** An absent hour is a gap or an unknown; which one depends on `node`. */
  hours: {
    t: number
    cloud_seconds: number
    cloud_missing_seconds: number
    node_seconds: number
    node_uploaded_seconds: number
    /** Prorated from the node's per-DAY byte figure -- see the endpoint. */
    node_bytes_estimate: number | null
  }[]
  node: {
    observed_utc: string | null
    age_seconds: number | null
    generation: number | null
    retention_hours: number | null
    /** Observed. The oldest hour the node reports holding -- draw the horizon here. */
    oldest_present_utc: string | null
    /** A forecast, not a boundary. Retention caps segment count, not clock age,
     *  so this sits later than the truth on any node that has missed a recording. */
    horizon_projected_utc: string | null
    uploads_audio: boolean
    disk_total_bytes: number | null
    disk_used_bytes: number | null
    high_water_frac: number | null
    bytes_per_hour: number | null
    /** What the card can actually keep, as opposed to what anyone asks of it.
     *
     *  The janitor takes the larger of the age cap and the high-water mark, so
     *  past this point raising retention changes nothing but what a screen
     *  claims. Null when the node has not reported its filesystem — offer
     *  nothing rather than guess. */
    achievable_retention_hours: number | null
  }
  throughput: {
    bytes_per_s: number | null
    window_hours: number
    sample_bytes: number
    sample_objects: number
    sample_audio_bytes: number
    /** False means: show no ETA. The sample contains no audio, so it bounds
     *  nothing about what an audio transfer would achieve. */
    representative_for_audio: boolean
    basis: string
  }
  caps: { max_range_hours: number; hours_returned: number; truncated: boolean }
}

/** A request that a node send up audio it is still holding.
 *
 *  Not a command: there is no inbound path to a node, so this is published by
 *  being written and the node picks it up on its next desired-state poll.
 */
export interface SignedObject {
  key: string
  kind: string
  size: number | null
  start_utc: string
  url: string
}

/** Presigned GETs for a batch of a mission's objects.
 *
 *  `OBJECT_URL_TTL` is 300 s, so a ten-minute selection outlives its own URLs
 *  and the player re-signs as it goes — in batches, rather than a round trip
 *  per 60 s segment. Rejections come back alongside rather than thrown: a
 *  selection can legitimately span a pending object or a gap, and failing the
 *  whole batch for one absent segment would make ten minutes unplayable
 *  because of a minute nobody asked about.
 */
export function objectUrls(missionId: string, keys: string[], download = false) {
  return api.post<{
    urls: SignedObject[]
    rejected: { key: string; reason: string }[]
    expires_in: number
  }>(`/v1/missions/${encodeURIComponent(missionId)}/objects:urls`,
    { keys, download })
}

/* --------------------------------------------------- importing a card */

export interface CardPresignItem {
  kind: 'audio' | 'reference'
  name: string
  start_utc: string
  size: number
  md5_b64: string
}

export interface CardPresignUrl {
  key: string
  url: string
  /** Bound into the signature, so the PUT must send them back exactly. Carries
   *  Content-MD5 always, and If-None-Match on anything under raw/ — which is
   *  what makes a duplicate fail with 412 instead of overwriting the archive
   *  of record. */
  headers: Record<string, string>
  expires_at: string
  /** This recording starts outside the deployment window. Stored and flagged
   *  either way — never dropped — but shown on the file's own row, because
   *  "which ones" is the question a batch count cannot answer. */
  attribution_conflict?: boolean
}

export interface CardPresignOut {
  /** Positional and parallel to the items sent, with null where one was
   *  refused — the same contract a node gets, so the retry logic is the same
   *  on both sides. */
  urls: (CardPresignUrl | null)[]
  rejected?: { index: number; reason: string; detail?: string }[]
  /** How many of this batch fall outside the deployment window. Stored and
   *  flagged either way; the count is what tells an operator the mission's
   *  dates are wrong rather than the files. */
  attribution_conflicts?: number
}

/** Presigned PUTs for one batch off a memory card.
 *
 *  Batched at 32 to match the receiver's limit, which matches the node's
 *  upload contract. `duplicate` in `rejected` is a success: keys are
 *  deterministic, so re-importing a card that was half-uploaded returns the
 *  same keys and the ones already stored come back refused. That is how an
 *  interrupted import resumes rather than duplicating.
 */
export function cardPresign(missionId: string, items: CardPresignItem[]) {
  return api.post<CardPresignOut>(
    `/v1/missions/${encodeURIComponent(missionId)}/card:presign`,
    { schema: 1, items })
}

/** Above this a file goes up in parts. Must stay at or below the receiver's
 *  own `MPU_THRESHOLD`, which refuses a single PUT larger than this. */
export const MPU_THRESHOLD = 64 * 1024 * 1024

/** One part. Chosen here rather than by the receiver because the file has to
 *  be hashed in part-sized pieces before the upload can even be started, and
 *  reading a 4 GiB file twice to find out the size first is minutes of disk
 *  for nothing. The receiver validates it. */
export const PART_SIZE = 64 * 1024 * 1024

export interface MpuStartOut {
  key: string
  /** Already stored. The same resumption story as `duplicate` on the presign
   *  path — deterministic keys mean re-importing a card finishes it rather
   *  than duplicating it. */
  duplicate?: boolean
  upload_id?: string
  part_size?: number
  parts?: number
  attribution_conflict?: boolean
}

export function mpuStart(
  missionId: string, item: CardPresignItem, partSize: number,
) {
  return api.post<MpuStartOut>(
    `/v1/missions/${encodeURIComponent(missionId)}/card:mpu-start`,
    { schema: 1, item, part_size: partSize })
}

export interface MpuSignedPart {
  part_number: number
  url: string
  headers: Record<string, string>
}

/** Signed a batch at a time, not all at once: a 4 GiB file is 64 parts and the
 *  URLs expire, so signing every part up front hands out URLs that are already
 *  dead by the time the upload reaches them. */
export function mpuSign(
  missionId: string, key: string, uploadId: string,
  parts: { part_number: number; md5_b64: string }[],
) {
  return api.post<{ parts: MpuSignedPart[]; expires_at: string }>(
    `/v1/missions/${encodeURIComponent(missionId)}/card:mpu-sign`,
    { schema: 1, key, upload_id: uploadId, parts })
}

export function mpuComplete(
  missionId: string, key: string, uploadId: string,
  parts: { part_number: number; etag: string }[],
) {
  return api.post<{ key: string; stored: boolean }>(
    `/v1/missions/${encodeURIComponent(missionId)}/card:mpu-complete`,
    { schema: 1, key, upload_id: uploadId, parts })
}

/** Throw away an upload that was stopped partway. Parts already sent are
 *  billed until this is called. */
export function mpuAbort(missionId: string, key: string, uploadId: string) {
  return api.post<{ aborted: boolean }>(
    `/v1/missions/${encodeURIComponent(missionId)}/card:mpu-abort`,
    { schema: 1, key, upload_id: uploadId })
}

/** Confirm a batch landed. Idempotent, so a retry after an ambiguous timeout
 *  is correct behaviour rather than a problem. */
export function cardComplete(missionId: string, keys: string[]) {
  return api.post<{ stored: string[]; missing: string[] }>(
    `/v1/missions/${encodeURIComponent(missionId)}/card:complete`,
    { schema: 1, keys })
}

export interface ArchiveJob {
  job_id: string
  compute_id: string
  mode: 'range' | 'rolling' | 'continuous'
  from_utc: string | null
  to_utc: string | null
  rolling_hours: number | null
  window_tz: string | null
  window_start_min: number | null
  window_end_min: number | null
  max_bytes: number | null
  bytes_stored: number
  segments_stored: number
  last_progress_at: string | null
  state: 'active' | 'paused' | 'done' | 'cancelled' | 'expired'
  expires_at: string
  created_at: string
  label: string | null
  closed_reason: string | null
  /** `video` is the camera's segments, as recorded: no codec, no bitrate. */
  format_kind: 'reduced' | 'video' | null
  format_codec: 'aac' | 'opus' | null
  format_bitrate_kbps: number | null
  /** Who asked for it, and who turned it off. The second comes from the audit
   *  trail rather than a column, so it cannot disagree with it. */
  requested_by_name: string | null
  closed_by_name: string | null
  resolved_from_utc: string | null
  resolved_to_utc: string | null
  window_open_now: boolean
  /** When the window next opens, for a job that is active but idle. */
  next_window_utc: string | null
}

/** How long a node keeps audio on its card before the janitor takes it.
 *
 *  `null` withdraws the instruction and returns the node to its own node.toml,
 *  rather than leaving the last pushed value in force for ever.
 *
 *  Sent as a single field. The receiver does the read-modify-write, unlike
 *  `setService` above — a browser-side merge of the desired-state document is
 *  what once built a whole document out of one field and nulled the rest.
 */
export function setRetention(computeId: string, hours: number | null) {
  return api.put<{ revision: number; retention_hours: number | null }>(
    `/v1/computes/${encodeURIComponent(computeId)}/retention`, { hours })
}

/** Whether the node on the other end can act on an archive job at all.
 *
 *  `revision_applied` shipped with the node's archive support, so a node that
 *  is demonstrably alive and has never reported one predates the pull path: a
 *  job for it is not slow, it is inert. */
export interface ArchiveNode {
  compute_id: string
  revision_applied: number | null
  observed_utc: string
  archive_capable: boolean
}

export interface ArchiveJobIn {
  mode: 'range' | 'rolling' | 'continuous'
  from_utc?: string
  to_utc?: string
  rolling_hours?: number
  window_tz?: string
  window_start?: string
  window_end?: string
  max_bytes?: number
  label?: string
  acknowledge_cost?: boolean
  format_kind?: 'reduced' | 'video'
  format_codec?: 'aac' | 'opus'
  format_bitrate_kbps?: number
}

/** Ask a node for a time range.
 *
 *  The server trims a request that reaches past what the node still holds and
 *  says so in `horizon.clipped`, rather than refusing it — so the caller must
 *  read the response back rather than assuming it got what it asked for.
 */
export function createArchiveJob(missionId: string, body: ArchiveJobIn) {
  return api.post<{
    job_id: string
    from_utc: string | null
    horizon: {
      oldest_available_utc: string | null
      clipped: boolean
      node_observed_utc: string | null
      /** Set on a video pull reaching past what the camera keeps. */
      video_note?: string
    }
  }>(`/v1/missions/${encodeURIComponent(missionId)}/archive-jobs`, body)
}

export function changeArchiveJob(
  missionId: string, jobId: string, verb: 'cancel' | 'pause' | 'resume',
) {
  return api.post<{ job_id: string; state: string }>(
    `/v1/missions/${encodeURIComponent(missionId)}/archive-jobs/`
    + `${encodeURIComponent(jobId)}:${verb}`, {})
}

export interface Arrivals {
  mission_id: string
  days: number
  by_day: { day: string; kind: string; count: number; bytes: number; stored: number }[]
  by_kind: { kind: string; count: number; bytes: number; latest: string | null }[]
  attribution_conflicts: number
}

export interface DesiredState {
  schema: number
  compute_id: string
  revision: number
  mission_id: string | null
  tier: string | null
  services: Record<string, string>
  config_overlay: string | null
  sw_version: string | null
  updated_at: string | null
  updated_by: string | null
}

/* ------------------------------------------------------------ desired state */

/** Turn one service on or off without disturbing the rest.
 *
 *  PUT /v1/computes/{id}/desired-state is a FULL REPLACE, not a merge --
 *  `services` defaults to {} server-side, so a PUT that omits it wipes every
 *  directive including `live`. This has already caused a real incident here:
 *  omitting `tier` silently downgraded a node mid-deployment. So the whole
 *  object is read, modified and written back, under If-Match.
 *
 *  A 404 from the GET is normal, not an error: it means no row exists yet, and
 *  the first write for a node must go without If-Match or it fails 428.
 */
export async function setService(
  computeId: string,
  service: string,
  value: string | null,
): Promise<DesiredState> {
  const path = `/v1/computes/${encodeURIComponent(computeId)}/desired-state`

  let current: DesiredState | null = null
  try {
    current = await api.get<DesiredState>(path)
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 404)) throw e
  }

  const services = { ...(current?.services ?? {}) }
  if (value === null) delete services[service]
  else services[service] = value

  const body = {
    mission_id: current?.mission_id ?? null,
    tier: current?.tier ?? null,
    services,
    config_overlay: current?.config_overlay ?? null,
    sw_version: current?.sw_version ?? null,
  }

  return api.put<DesiredState>(
    path,
    body,
    current ? `"${current.revision}"` : undefined,
  )
}

/* --------------------------------------------------------------- listening
 *
 * Addressed by mission, not by compute, and that is the whole point.
 *
 * `setService` above writes the node's desired-state document, which also
 * carries its tier, its retention overlay and which mission it attributes
 * uploads to. That document is staff-only, rightly: handing a site manager the
 * control plane so they can hear their own farm is not a trade worth making.
 * These two say one thing each, are gated on `listen` + `listening.live`, and
 * delegate to the same writer on the server rather than reimplementing it.
 */

export interface LiveState {
  schema: number
  mission_id: string
  live: string
}

/** One sensor a node can stream live. `id` is '' for the primary (the
 *  hydrophone) and e.g. 'moth' for a HydroMoth beside it; each has its own
 *  playlist, `live.m3u8?stream=<id>`. One entry means nothing to switch. */
export interface LiveStream {
  id: string
  primary: boolean
  sensor: string | null
  live_service: string
  desired: string
  present?: boolean
  capturing?: boolean
  /** 'camera' is video with the node's audio muxed in, which an <audio>
   *  player cannot show. Staff only; the server leaves it out for customers. */
  kind?: 'audio' | 'camera'
}

/** GET /v1/missions/{id}/live, for one stream. */
export interface LiveInfo {
  schema: number
  mission_id: string
  stream: string
  window_seconds: number
  segments: number
  newest: string | null
  live_service_desired: string
  playlist: string
  streams?: LiveStream[]
}

function streamQuery(stream: string | undefined) {
  return stream ? `?stream=${encodeURIComponent(stream)}` : ''
}

/** Starts this stream and stops every other on the node, in one revision:
 *  the sensor switch. '' is the hydrophone. */
export function startLive(missionId: string, stream = '') {
  return api.post<LiveState>(
    `/v1/missions/${encodeURIComponent(missionId)}/live:start${streamQuery(stream)}`)
}

/** Stopping matters as much as starting. A node left streaming uploads a
 *  segment every second to nobody, on a farm uplink, until its own half-hour
 *  limit cuts it off. Without `stream`, stops every stream the node has. */
export function stopLive(missionId: string, stream?: string) {
  return api.post<LiveState>(
    `/v1/missions/${encodeURIComponent(missionId)}/live:stop${stream === undefined ? '' : `?stream=${encodeURIComponent(stream)}`}`)
}

/* ------------------------------------------------------------------ camera
 *
 * A node with a camera keeps rolling video on its card (about 12 h), streams it
 * live on request, and takes stills when asked. Staff only for now.
 *
 * Live is the same `startLive`/`stopLive` with stream 'cam', so the sensor
 * switch's either/or holds: watching the camera stops hydrophone live. Video
 * off the card is an archive job with format_kind 'video'. A still is its own
 * endpoint rather than a `setService` merge, for the reason `setRetention`
 * gives -- and because a still request is not an edit of the node's desired
 * state at all: the server keeps it in its own table and hands it to the node
 * when it polls.
 */

export const CAMERA_STREAM = 'cam'

/** The camera as the node last reported it. */
export interface CameraInfo {
  id: string
  sensor: string | null
  present?: boolean
  capturing?: boolean
  width?: number
  height?: number
  framerate?: number
  live_service?: string
}

export interface CameraStill {
  request_id: string
  resolution: 'video' | 'full'
  /** `expired` is also how a failed capture shows: the node says nothing
   *  else when a picture does not come out. */
  state: 'pending' | 'arrived' | 'expired' | 'cancelled'
  requested_at: string
  requested_by: string | null
  not_after: string
  key: string | null
  arrived_at: string | null
  size: number | null
  /** Presigned, five minutes. Only on an arrived still. */
  url: string | null
}

/** GET /v1/computes/{id}/camera: everything the camera card shows. */
export interface CameraDoc {
  compute_id: string
  mission_id: string | null
  camera: (CameraInfo & { observed_utc: string | null; live_desired: string }) | null
  stills: CameraStill[]
  /** Camera segments that have reached object storage, newest first. */
  video: { key: string; start_utc: string | null; size: number | null; state: string }[]
  settings: {
    effective: { width?: number; height?: number; framerate?: number } | null
    editable: boolean
    why_not?: string
  }
  controls: CameraControlsView
}

/** Picture controls. Every key optional; an absent one is the camera's own
 *  default, so `{}` is the camera as it comes. The node's
 *  `camera.parse_controls` decides the ranges, and the receiver mirrors them. */
export interface CameraControls {
  af_mode?: 'continuous' | 'auto' | 'manual'
  /** Dioptres, 1 / metres: 0 is infinity, 2 is 50 cm. Implies manual. */
  lens_position?: number
  af_range?: 'normal' | 'macro' | 'full'
  zoom?: number
  zoom_center?: [number, number]
  awb?: 'auto' | 'incandescent' | 'tungsten' | 'fluorescent' | 'indoor'
    | 'daylight' | 'cloudy' | 'custom'
  /** Red and blue. Switches automatic white balance off. */
  awb_gains?: [number, number]
  brightness?: number
  contrast?: number
  saturation?: number
  sharpness?: number
  ev?: number
  hflip?: boolean
  vflip?: boolean
}

/** What was asked, beside what the node says it runs with. */
export interface CameraControlsView {
  asked: CameraControls
  asked_at: string | null
  asked_by: string | null
  /** Null until the node runs software that knows controls. */
  effective: CameraControls | null
  /** Control -> why the node would not apply it. */
  refused: Record<string, string>
  /** `local` while someone at the node overrides the receiver by hand. */
  source: 'receiver' | 'local' | null
  supported: boolean
  why_not?: string
}

/** Replace the camera's controls. Like setRetention, a dedicated endpoint and
 *  not a desired-state merge: the controls are a row of their own, written
 *  into the node's document when it polls, so nobody's If-Match is
 *  disturbed. Each change restarts the node's video for a few seconds. */
export function setCameraControls(computeId: string, controls: CameraControls) {
  return api.put<{ controls: CameraControls; applies: string }>(
    `/v1/computes/${encodeURIComponent(computeId)}/camera/controls`, controls)
}

export function takeStill(computeId: string, resolution: 'video' | 'full' = 'video') {
  return api.post<{ request_id: string; not_after: string; state: string }>(
    `/v1/computes/${encodeURIComponent(computeId)}/camera:still`, { resolution })
}

export function cancelStill(computeId: string, requestId: string) {
  return api.post<{ request_id: string; state: string }>(
    `/v1/computes/${encodeURIComponent(computeId)}/camera/stills/`
    + `${encodeURIComponent(requestId)}:cancel`, {})
}

/** Ask the node for the camera's footage over a range, at most six hours. */
export function pullVideo(missionId: string, fromUtc: string, toUtc: string) {
  return createArchiveJob(missionId, {
    mode: 'range', from_utc: fromUtc, to_utc: toUtc, format_kind: 'video',
    label: 'Camera video',
  })
}

/** Take a unit out of service, or put it back.
 *
 *  Not a delete, and the UI should never offer one: every object key and every
 *  past mission is attributed to this compute row. Retiring hides it from the
 *  landing page and closes its active mission; restoring is one call.
 *
 *  It also stops the node authenticating -- `revoked_at` is what node token
 *  lookup checks -- which is the correct meaning of "out of service" and the
 *  reason the confirmation says so in words.
 */
export async function retireCompute(computeId: string, retired: boolean) {
  const verb = retired ? 'retire' : 'restore'
  return api.post<{ schema: number; missions_archived?: string[] }>(
    `/v1/computes/${encodeURIComponent(computeId)}/${verb}`,
  )
}

/** End or reclassify a deployment.
 *
 *  `recovered` is the kit came out of the water -- the normal end of an
 *  episode, and the honest record of it. `archived` is this was never a real
 *  deployment, which is the one to use on commissioning tests. Both close the
 *  mission's window; the server ends any overlapping missions and audits it.
 *
 *  Note this is a statement about the physical world, not a display setting.
 *  The missions table is the location history R2.2 answers from, so ending one
 *  says the hardware stopped being somewhere.
 */
/** `at` is when the transition happened, not when it was recorded.
 *
 *  A node's mission is closed by the successor that displaced it, so `now` is
 *  the truth and the argument is omitted. A card pulled on Tuesday and
 *  processed on Friday is not: stamping Friday widens the deployment window by
 *  three days, and the window decides which recordings are attributable.
 */
export async function transitionMission(
  missionId: string, state: string, at?: string,
) {
  return api.post<{ schema: number; mission_id: string; state: string }>(
    `/v1/missions/${encodeURIComponent(missionId)}/transition`,
    at ? { state, at } : { state },
  )
}

/** Correct the facts of a deployment that already exists.
 *
 *  This is how a position gets fixed after the boat has been out. Planning
 *  accuracy is a circle about a hundred metres across; the mooring is known
 *  exactly once someone has stood over it, and that correction has to be
 *  possible without re-commissioning — which would mint a second mission and
 *  leave the first as litter.
 *
 *  If-Match on the mission's revision, and the caller must handle 412: two
 *  people correcting the same deployment from different boats must not
 *  silently overwrite each other. 428 means the revision was not sent at all,
 *  which is a bug here rather than something a user can act on.
 *
 *  The node sees the change on its next poll. The desired-state ETag is a hash
 *  of the response body rather than of `desired_state.revision`, so editing a
 *  mission's position changes what the node is served without anyone having to
 *  write desired state as well.
 */
export async function patchMission(
  missionId: string,
  revision: number,
  patch: Partial<Pick<Mission,
    'site_id' | 'project_id' | 'latitude' | 'longitude' | 'deploy_depth_m' |
    'tier' | 'analogue_gain_db' | 'gain_setting' | 'notes' | 'started_at' |
    'ended_at' | 'activate_after'>>,
  clear: string[] = [],
) {
  return api.patch<Mission & { revision: number }>(
    `/v1/missions/${encodeURIComponent(missionId)}`,
    clear.length ? { ...patch, clear } : patch,
    `"${revision}"`,
  )
}

/** What POST /v1/redeploy takes: the same box, a new place. */
export interface RedeployIn {
  compute_id: string
  site?: string
  site_id?: string
  locality_no?: number
  project_id?: string
  lat?: number
  lon?: number
  depth_m?: number
  microphone_id?: string
  tier?: string
  analogue_gain_db?: number
  notes?: string
  purpose?: 'deployment' | 'test'
  /** Omitted means "as soon as it next makes contact", which is right for a
   *  move happening now. */
  activate_after?: string
}

export interface RedeployOut {
  mission_id: string
  site_id: string
  site_created: boolean
  microphone_id: string | null
  replaces: string | null
  activate_after: string | null
  next?: string
}

/** Arrange a node's next deployment before it happens.
 *
 *  Deliberately not commissionNode(). Commissioning is for hardware that is not
 *  here yet: it mints a claim code and leaves the compute unbound, because the
 *  board is claimed at first boot. This unit is already enrolled, so a claim
 *  code would be a credential nothing can redeem.
 *
 *  Nothing is sent to the node and nothing is asked of it. The mission is
 *  created `planned`, which the receiver refuses uploads to, so an arrangement
 *  made on Monday cannot swallow Tuesday's recordings. It starts by itself when
 *  the node next makes contact at or after `activate_after`, and the deployment
 *  it replaces is closed at that same instant.
 */
export function redeploy(body: RedeployIn) {
  return api.post<RedeployOut>('/v1/redeploy', body)
}

/* ---------------------------------------------------------------- helpers */

export function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  if (n < 1024) return `${n} B`
  const u = ['kB', 'MB', 'GB', 'TB']
  let v = n / 1024
  let i = 0
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024
    i++
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}

/** Relative time, and deliberately never rendered as a presence indicator.
 *  There is no broker and no node_state table, so nothing in this system can
 *  distinguish a node that is off from one whose uplink is down or whose tier
 *  simply has nothing to send this hour. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 0) return 'just now'
  if (s < 60) return `${Math.floor(s)}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

/** An instant, on the clock the viewer has chosen, with that clock named.
 *
 *  The zone used to be implicit -- `toLocaleString` with no `timeZone`, which
 *  is local and says nothing. That reads fine until it sits next to something
 *  bucketed in UTC, and then two correct numbers two hours apart are on screen
 *  with nothing to explain the gap. See time.ts. */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime() / 1000
  return `${new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    timeZone: currentZone() === 'utc' ? 'UTC' : undefined,
  }).format(new Date(iso))} ${zoneLabel(t)}`
}

/** R2.8: technicians read labels, not identifiers. Always this, never the
 *  bare id, and never the id when a name exists. */
export function nodeName(n: { display_name: string | null; compute_id: string }): string {
  return n.display_name || n.compute_id
}

/* --- the acoustic log -----------------------------------------------------
 *
 * Served by app/acoustics.py, which is a thin reader over the analytics schema
 * phoniq-analyzer owns. Nothing here is computed in the browser and nothing is
 * computed in the receiver either: a score is the analyzer's, made against a
 * two-sided retrospective baseline that the node producing the sound could not
 * have had, and the distinction matters enough that both are kept.
 *
 * `analysed: false` is a real answer and not an error. A site whose analyzer
 * has never run says so, with what would fix it, rather than returning an empty
 * list that reads as "nothing happened". */

export interface LogSensor {
  mission_id: string
  score: number | null
  verdict: 'event' | 'elevated' | 'background' | 'not_deployed' | 'unknown'
  direction: 'louder' | 'quieter' | 'mixed' | null
  driver_bands_hz: number[]
  corroboration: 'corroborated' | 'local' | 'unwitnessed' | null
  n_agree: number | null
  /** How many sensors could hear the driver band at all. A 2/2 on a
   *  low-frequency event is not weaker than a 5/6 on a mid-band one — it means
   *  only two sensors are not high-passed above it. */
  n_could_hear: number | null
  n_obs: number | null
  deployment_state: 'deployed' | 'dry' | 'unknown' | null
  spread_db: number | null
  /** Present on the hour endpoint, absent on the log list. How much of the
   *  hour was actually recorded — a verdict from nine minutes and one from
   *  sixty are not the same claim. */
  n_minutes?: number | null
  level_db?: number | null
  /** How loud it actually was, in dB re 1 µPa, summed in the power domain over
   *  `spl_band_hz`. Hour endpoint only, and ABSENT rather than null whenever
   *  the fleet may not say — which is most of it: every calibration term on
   *  the two Pi nodes is an estimate, and the card recorders have no
   *  `full_scale_dbv` at all. The server decides; this type only prints. */
  spl_db?: number | null
  spl_band_hz?: [number, number]
  spl_n_bands?: number
  /** The basis string verbatim from the receiver, carried with the figure so a
   *  screen cannot print the level and leave the caveat behind. */
  spl_basis?: string
  spl_uncertainty_db?: number | null
  /** The whole spectrum behind the verdict, at decidecade resolution. Hour
   *  endpoint only.
   *
   *  It exists because the low-frequency case cannot be confirmed by ear.
   *  Human hearing falls away steeply below about 100 Hz, so a 32 Hz ship
   *  tonal can sit 30 dB above ambient on the hydrophone and be inaudible on a
   *  laptop — someone who presses play hears surface water and concludes
   *  nothing happened.
   *
   *  `band_z` is how UNUSUAL each band was; `band_excess_db` is how much
   *  louder. They disagree, and the disagreement is the point: a band can be
   *  14 dB up and entirely normal for that place. NULL entries are bands the
   *  sensor could not measure — absent, never zero. */
  band_centres_hz?: (number | null)[]
  band_z?: (number | null)[]
  band_excess_db?: (number | null)[]
}

export interface LogVessel {
  mmsi: number
  name: string | null
  role: string | null
  distance_km: number | null
  sog_kn: number | null
  status: string | null
  /** Zero when the position came from the AIS passing query, because the vessel
   *  reported inside this hour. Non-zero only on the snapshot fallback, where
   *  the register says where a vessel was LAST SEEN rather than where it was
   *  during the hour — weak evidence that has to look weak. */
  staleness_s: number | null
  /** Closest approach during the hour, in metres. The number that matters:
   *  received level falls with range, so a 130 m ship idling at 1.3 km and a
   *  service boat working at 19 m are entirely different events. */
  closest_m?: number | null
  max_sog_kn?: number | null
  length_m?: number | null
  /** Whether the register classes it as part of the aquaculture fleet. A
   *  wellboat working the pens and a coaster passing by are different events
   *  even at the same range. */
  aqua_fleet?: boolean | null
  /** Position reports inside the hour. One is a vessel clipping the edge of
   *  the window; twenty is one that stayed. */
  n_points?: number | null
  /** Where it was at closest approach, and the path it took through the hour.
   *  Returned by the hour endpoint only — a fortnight of tracks is megabytes
   *  nobody scrolling a list asked for.
   *
   *  Track rows are `[t, lat, lon, sog, cog, dist_m]`, positional rather than
   *  keyed for the same reason the register sends them that way: object keys
   *  would be most of the payload. */
  closest_lat?: number | null
  closest_lon?: number | null
  track?: [number, number, number, number | null, number | null,
           number | null][]
}

export interface SiteOperation {
  event_type: string
  method: string | null
  /** How the register concluded it: `ais_voyage`, `fdir`, `dwell`. An
   *  inference and a record are different claims. */
  basis: string | null
  vessel_name: string | null
  mmsi: number | null
  dwell_min: number | null
  confirmed: boolean
}

export interface LogHour {
  hour_epoch: number
  at: string
  /** Calendar date at the FARM (see `SiteLog.timezone`), not in UTC and not in
   *  the reader's zone. Group on this and label with the same zone, or the
   *  grouping and the heading will disagree. */
  day: string
  verdict: string
  score: number | null
  direction: string | null
  driver_bands_hz: number[]
  corroboration: string | null
  n_agree: number | null
  n_could_hear: number | null
  n_notable: number
  sensors: LogSensor[]
  vessels: LogVessel[]
  operations: SiteOperation[]
}

/* A document published for a site: written once, dated, and served only to a
 * signed-in caller entitled to this site's log. The bytes never come back in
 * JSON -- `GET /v1/sites/{id}/reports/{slug}` answers with the PDF itself, so
 * an <a> to that path is all the viewer needs and the session cookie carries
 * the authorisation. */
export interface SiteReport {
  slug: string
  title: string
  subtitle: string | null
  /** ISO date, in the site's own terms: the day the report is ABOUT. */
  report_date: string
  media_type: string
  size_bytes: number
  published_at: string
  /** The moment in the recording this document is about, ISO, or null.
   *
   *  A timestamp and not a clip id. A clip is a saved object that can be
   *  deleted; a report that pointed at one would eventually offer a button
   *  that opens nothing. The recording is addressable by time on its own, so
   *  the listen page can stand at this moment and say for itself whether the
   *  audio is still there. */
  listen_at: string | null
  /** Which hydrophone that moment was recorded on, or null.
   *
   *  A site can carry more than one, and they do not hear the same thing.
   *  Without this the listen page opens on whichever it chose for itself,
   *  which on a quiet sensor reads as "nothing was recorded that day". */
  listen_mission: string | null
}

export interface SiteReports {
  schema: 1
  site_id: string
  reports: SiteReport[]
}

/** Where a report's bytes live. Same origin, so the session cookie goes with
 *  it; `download` swaps the disposition from inline to attachment. */
export function reportUrl(siteId: string, slug: string, download = false): string {
  const base = `/v1/sites/${encodeURIComponent(siteId)}`
    + `/reports/${encodeURIComponent(slug)}`
  return download ? `${base}?download=1` : base
}

/** The listen page, standing at one moment.
 *
 *  Seconds since the epoch rather than an ISO string, because the page has to
 *  do arithmetic with it and an ISO string in a query is one timezone bug
 *  waiting for a reader in another country. The page resolves which day that
 *  is in the SITE's zone, which is the only zone the strip is ever drawn in. */
export function listenAtUrl(siteId: string, isoOrEpoch: string | number,
                            missionId?: string | null): string {
  const epoch = typeof isoOrEpoch === 'number'
    ? isoOrEpoch
    : Math.floor(new Date(isoOrEpoch).getTime() / 1000)
  const sensor = missionId ? `&hydrofon=${encodeURIComponent(missionId)}` : ''
  return `/sites/${encodeURIComponent(siteId)}/lytt?tid=${epoch}${sensor}`
}

export interface SiteLog {
  schema: 1
  site_id: string
  analysed: boolean
  reason?: string
  needs?: string
  /** The farm's own zone, e.g. `Europe/Oslo`. `day` on every hour is a date in
   *  THIS zone, so anything rendering those hours must format in it too --
   *  formatting in the browser's zone instead is what made one local day emit
   *  two date headings. Absent when the site has no zone set; then UTC. */
  timezone?: string | null
  from: string
  to: string
  hours: LogHour[]
  truncated?: boolean
  calibration?: Record<string, Calibration>
  coverage: {
    sensors: number
    sensors_reporting: number
    hours_with_data: number
    hours_in_window: number
    hours_not_deployed: number
  }
  operations: SiteOperation[]
  features?: { vessels: boolean }
}

export interface ContextSeries {
  provider: string
  metric: string
  unit: string | null
  provenance: 'direct' | 'derived' | 'estimated'
  source: string
  points: [number, number][]
}

export interface ContextProviderStatus {
  provider: string
  label: string
  ok: boolean
  reason: string | null
  detail: string | null
  /** What would make this provider work. The difference between a blank panel
   *  and a task. */
  needs: string | null
  checked_at: string | null
}

export interface SiteContext {
  schema: 1
  site_id: string
  analysed: boolean
  from: string
  to: string
  series: ContextSeries[]
  moments: { provider: string; metric: string; at: number; provenance: string }[]
  providers: ContextProviderStatus[]
}

export interface IndexResult {
  index: string
  mission_id: string
  period: string
  start: string
  end: string
  /** Null is a real answer — no dark period at this latitude in June, too
   *  little coverage, no baseline. Render `reason`, never a blank. */
  score: number | null
  grade: string
  reason: string
  coverage: number | null
  components: Record<string, unknown>
  detail: Record<string, unknown>
  /** Never optional. A score is a rule somebody chose, and this one carries a
   *  claim about animal welfare the evidence does not support. */
  assumptions: string[]
}

export interface SiteIndices {
  schema: 1
  site_id: string
  analysed: boolean
  results: IndexResult[]
}

export interface HourMedia {
  key: string
  mission_id: string
  kind: string
  size: number | null
  start_utc: string | null
  /** False for the 1-minute `reduced` proxy. A listener should know whether
   *  they are hearing the recording or a 64 kbit rendering of it. */
  lossless: boolean
}

export interface SiteHour {
  schema: 1
  site_id: string
  hour_epoch: number
  analysed: boolean
  at: string
  /** Where the farm is, so a map frames itself without a second call. */
  site?: { lat: number | null; lon: number | null }
  calibration: Record<string, Calibration>
  sensors: LogSensor[]
  media: HourMedia[]
  vessels: LogVessel[]
  context: { provider: string; metric: string; unit: string | null;
             provenance: string; at: number; value: number }[]
  operations: SiteOperation[]
  /** Which of this farm's purchases shaped the payload, so the absence of a
   *  panel has a stated cause instead of looking like absence of data. */
  features?: { vessels: boolean }
}

export const acoustics = {
  log: (siteId: string, q: { days?: number; until?: string; min_score?: number;
                             corroborated_only?: boolean; limit?: number } = {}) =>
    api.get<SiteLog>(`/v1/sites/${encodeURIComponent(siteId)}/log${qs(q)}`),
  context: (siteId: string, q: { days?: number; until?: string } = {}) =>
    api.get<SiteContext>(`/v1/sites/${encodeURIComponent(siteId)}/context${qs(q)}`),
  indices: (siteId: string, q: { index?: string; days?: number; until?: string } = {}) =>
    api.get<SiteIndices>(`/v1/sites/${encodeURIComponent(siteId)}/indices${qs(q)}`),
  hour: (siteId: string, hourEpoch: number) =>
    api.get<SiteHour>(`/v1/sites/${encodeURIComponent(siteId)}/hours/${hourEpoch}`),
}

/* ------------------------------------------------------------------ klipp
 *
 * A clip is a bit of sound somebody at this farm kept, and there are two
 * physically different things behind that one word.
 *
 * A **live clip** is a file: somebody pressed Record while the stream was
 * playing and the browser PUT the encoded audio into the bucket. `key` points
 * at it, and the player fetches and decodes that one object.
 *
 * A **mark** is a pair of instants pointing at a recording that was already in
 * storage. There is no file, `key` is null, and playing it means streaming the
 * day's own segments windowed to the span -- which is what the archive player
 * does anyway. That is why saving one is instant at any length: nothing is
 * encoded and nothing is uploaded.
 *
 * The distinction matters to the player and to almost nothing else, so
 * everywhere a person reads it, both are just "klipp".
 */
export interface SiteClip {
  clip_id: string
  /** Which hydrophone recorded it. A farm can have two in the water, and a
   *  span of time means nothing without saying which one heard it. */
  mission_id: string
  started_utc: string
  duration_s: number
  source: 'live' | 'archive'
  /** What the person keeping it said it was. The reason a colleague opening
   *  this is better off than they would be scrubbing the day themselves. */
  note: string | null
  /** The creator's email, and their display name when they have one. */
  created_by: string
  created_by_name: string | null
  /** Their user id, so a page can tell "mine" from "a colleague's" without
   *  comparing email strings. */
  created_by_id: string
  created_at: string
  edited_at: string | null
  /** Null on a mark. The player branches on exactly this. */
  key: string | null
  size: number | null
  state: string | null
  gain_db: number | null
}

export const klipp = {
  /** Everything kept at this farm, newest first, across both hydrophones.
   *
   *  Not filtered by day, and that is what makes the list an album rather than
   *  a second copy of the strip: the strip can only draw the day it is
   *  showing, and a clip somebody saved three weeks ago is the one most worth
   *  being able to find. */
  list: (siteId: string) =>
    api.get<{ items: SiteClip[] }>(
      `/v1/sites/${encodeURIComponent(siteId)}/clips`),

  /** Keep a span of a recording. Writes a row; moves no audio. */
  mark: (siteId: string, body: {
    mission_id: string
    started_utc: string
    duration_s: number
    note?: string | null
  }) => api.post<SiteClip>(`/v1/sites/${encodeURIComponent(siteId)}/clips`, body),

  /** Correct the note. Only the note: moving the span would make a link
   *  somebody already sent answer with different audio than they heard. */
  note: (clipId: string, note: string | null) =>
    api.patch<SiteClip>(`/v1/clips/${encodeURIComponent(clipId)}`, { note }),

  remove: (clipId: string) =>
    api.del<{ clip_id: string }>(`/v1/clips/${encodeURIComponent(clipId)}`),
}


/* ---------------------------------------------------------------- Loggbok
 *
 * The daily round and the weekly roundup. The receiver derives the cards and
 * phoniq-event holds the answers; from here it is one document per day.
 *
 * THE CATALOGUE ARRIVES WITH THE DATA and is not retyped here. A hardcoded copy
 * of the answer chips in this file is how a tapped label and a stored key end
 * up disagreeing the first time one side is edited -- and the record would then
 * say a farm answered something nobody ever offered them. `LoggbokSvar` is a
 * shape, never a list.
 */

export interface LoggbokSvar {
  key: string
  label: string
}

export interface LoggbokKatalog {
  forslag: Record<string, { navn: string; source: string; svar: LoggbokSvar[] }>
  skrevet: Record<string, { navn: string; svar: LoggbokSvar[] }>
  behandling_felt: {
    o2_trenging: LoggbokSvar[]
    velferd: string[]
  }
  avbruddskriterier: LoggbokSvar[]
  avvik_kategorier: LoggbokSvar[]
  severities: string[]
}

/** One thing the machine noticed, phrased as a question.
 *
 *  `evidence` is deliberately loose: it differs per source and the screen shows
 *  it as one muted line. What matters is that it is SHOWN -- «41 m · 18
 *  posisjoner · AIS» -- because an inference has to look like an inference. It
 *  is the same reason the receiver carries `basis` and `confirmed` separately
 *  rather than collapsing them into one verdict. */
export interface LoggbokForslag {
  proposal_id: string
  kind: string
  source: string
  source_key: string
  occurred_at: string | null
  headline: string
  evidence: Record<string, unknown>
  answers: LoggbokSvar[]
  state: 'open' | 'answered' | 'dismissed'
  /** 0-100: hvor mye denne saken trenger et menneske i dag. Regnet av
   *  mottakeren, som har tallene den hviler på, og IKKE hvor høyt noe var -- en
   *  dødelighetstopp er stille og ligger øverst. Køen er sortert på den før den
   *  kommer hit; feltet er med for at skjermen skal kunne si hvorfor. */
  priority: number
  dismissed_by: string | null
  dismissed_at: string | null
  dismiss_reason: string | null
}

export interface LoggbokAvvik {
  strakstiltak: string | null
  rotaarsak: string | null
  forebyggende: string | null
  evaluering: string | null
  category: string
  alvorlighet: number | null
  varslet: boolean
  varslet_at: string | null
  due_on: string | null
  closed_at: string | null
  closed_by: string | null
  /** How many of the four are filled. Counted by the server so the page never
   *  has to decide what "enough" means -- the only rule about that is a CHECK
   *  constraint, and it is about closing, not about progress. */
  utfylt: number
}

export interface LoggbokHendelse {
  entry_id: string
  kind: string
  occurred_at: string | null
  scope: 'site' | 'pens'
  /** 'bekreftet' -- confirmed what the machine saw. 'egen' -- written
   *  unprompted. Two claims of very different weight, and the page must never
   *  draw them the same. */
  via: 'bekreftet' | 'egen'
  answer: string | null
  note: string | null
  detail: Record<string, unknown>
  severity: 'notat' | 'observasjon' | 'avvik'
  assigned_to: string | null
  recorded_by: string | null
  recorded_at: string
  edited_at: string | null
  supersedes: string | null
  pens: { pen_id: string; pen_no: string }[]
  proposals: string[]
  comments: { comment_id: string; body: string; author: string
              created_at: string }[]
  seen_by: string[]
  deviation: LoggbokAvvik | null
}

export interface LoggbokDag {
  schema: 1
  site_id: string
  on: string
  timezone: string | null
  pens: { pen_id: string; pen_no: string }[]
  /** False when phoniq-event is not configured or not answering and there is
   *  no recent day to fall back on. The page says so rather than drawing an
   *  empty round, because a blank that means "nothing happened" and a blank
   *  that means "we could not ask" must never look alike. */
  available: boolean
  stale?: boolean
  reason?: string
  round: { completed_at: string | null; completed_by: string | null
           n_open: number | null; n_answered: number | null
           note: string | null }
  proposals: LoggbokForslag[]
  entries: LoggbokHendelse[]
  elsewhere: { kind: string; text: string; href: string }[]
  counts: { open: number; answered: number; dismissed: number }
  katalog: LoggbokKatalog
}

/** En hendelse slik UKA sender den, som er en smalere ting enn dagens.
 *
 *  Uka bærer `on` -- hvilken dag raden hører til, som dagsvisningen allerede
 *  vet -- og utelater tråden, sett-av og avviksfeltene, fordi den lister sju
 *  dager og ikke skal dra en kommentarhistorikk med seg per rad.
 *
 *  Egen type og ikke `LoggbokHendelse & { on: string }`: den andre formen ville
 *  lovet felter som ikke er der, og en type som lyver er verre enn to typer. */
export interface LoggbokUkeHendelse {
  entry_id: string
  on: string
  kind: string
  occurred_at: string | null
  severity: 'notat' | 'observasjon' | 'avvik'
  via: 'bekreftet' | 'egen'
  answer: string | null
  note: string | null
  detail: Record<string, unknown>
  recorded_by: string | null
  pens: { pen_id: string; pen_no: string }[]
}

export interface LoggbokGap {
  kind: string
  severity: 'avvik' | 'advarsel' | 'info'
  text: string
  on: string | null
  goto: 'dag' | 'uke'
  entry_id?: string
  antall?: number
  pens?: { pen_id: string; pen_no: string }[]
}

export interface LoggbokUke {
  schema: 1
  site_id: string
  available: boolean
  reason?: string
  iso_year: number
  iso_week: number
  from: string
  to: string
  timezone: string | null
  pens: { pen_id: string; pen_no: string }[]
  rutine: { on: string; runde: boolean; hendelser: number; apne: number
            framtid: boolean }[]
  gaps: LoggbokGap[]
  entries: LoggbokUkeHendelse[]
  avvik: (LoggbokAvvik & { entry_id: string; on: string
                           headline: string | null })[]
  per_merd: { pen_id: string; pen_no: string; n: number; avvik: number
              laksvel: boolean; kinds: Record<string, number> }[]
  rounds_done: number
  signed: { at: string; by: string; note: string | null } | null
  katalog: LoggbokKatalog
}

export function loggbokPath(siteId: string, on?: string) {
  return `/v1/sites/${encodeURIComponent(siteId)}/loggbok`
    + (on ? `?on=${encodeURIComponent(on)}` : '')
}

export function loggbokUkePath(siteId: string, uke?: string) {
  return `/v1/sites/${encodeURIComponent(siteId)}/loggbok/uke`
    + (uke ? `?uke=${encodeURIComponent(uke)}` : '')
}

const bok = (siteId: string) =>
  `/v1/sites/${encodeURIComponent(siteId)}/loggbok`

/** Every write answers with the WHOLE day, so the caller never has to guess
 *  what it did. welfare.py's shape and its reason: the screen is a set of cards
 *  whose states depend on each other -- answering one closes a proposal, may
 *  open an avvik, and moves the counts in the header. */
/** Ett fartøy, som en linje på en klokke.
 *
 *  Punktene er `[t, lat, lon, sog_kn, cog_deg, dist_m]`, eldste først, med `t`
 *  i epoch-sekunder. Samme seks felt som `LogVessel.track`, fordi det er den
 *  samme raden i `analytics.hour_vessels` — timene er bare sydd sammen til én
 *  linje her framfor å bli tegnet hver for seg. */
export interface SporFartoy {
  mmsi: number
  name: string | null
  aqua_fleet: boolean | null
  closest_m: number | null
  length_m: number | null
  track: [number, number, number, number | null, number | null,
          number | null][]
}

/** Ett besøk: ringene, sporene og lyden, på én klokke.
 *
 *  `from`/`to` er epoch-sekunder og ikke datoer, fordi alt som leser dem —
 *  skinnen, spennspilleren, interpolasjonen mellom to AIS-punkter — er regning
 *  på en klokke og ikke en dato som skal formateres. */
export interface LoggbokSpor {
  schema: 1
  site_id: string
  on: string
  timezone: string | null
  site: { lat: number | null; lon: number | null }
  from: number
  to: number
  /** Skipet kortet handler om, når kortet navnga ett. */
  mmsi: number | null
  pens: Pen[]
  structures: Structure[]
  /** Hvilke ringer som holder fisk denne dagen, og ingenting annet om dem.
   *
   *  Stimen tegnes av denne, og EN TOM RING ER STILL -- se `sverm.ts`, som
   *  tjener inn hele figuren på den ene regelen. Fisk som svømmer i en ring
   *  anlegget har brakklagt er et bilde som motsier anleggets egen plan, på en
   *  skjerm noen er i ferd med å signere. */
  occupied_pens: string[]
  vessels: SporFartoy[]
  media: HourMedia[]
  /** `[epoch, dBFS]` per minutt. RELATIV med vilje: dette er nodens egen
   *  bredbåndsverdi, som er et nivå i opptaket og ikke et nivå i sjøen. Om det
   *  får sies absolutt er mottakerens avgjørelse (R5.3), og kurven tar den
   *  ikke -- den svarer bare på «hvor i disse tolv timene er det noe å høre»,
   *  som er en påstand som ikke trenger kalibrering. */
  levels: [number, number][]
  calibration: Record<string, Calibration>
  /** Sant når minuttlista traff taket. Sagt høyt framfor å overlates til å
   *  gjettes ut fra en kort liste: et besøk uten lagret lyd og et besøk der vi
   *  ikke sendte lyden ser like ut herfra. */
  media_truncated: boolean
}

export const loggbok = {
  skriv: (siteId: string, body: {
    on_date: string
    kind: string
    occurred_at?: string | null
    answer?: string | null
    note?: string | null
    severity?: 'notat' | 'observasjon' | 'avvik'
    pen_ids?: string[]
    proposal_ids?: string[]
    detail?: Record<string, unknown>
    category?: string | null
    alvorlighet?: number | null
    strakstiltak?: string | null
    due_on?: string | null
  }) => api.post<LoggbokDag>(bok(siteId), body),

  avvis: (siteId: string, proposalId: string,
          body: { on_date: string; reason?: string | null }) =>
    api.post<LoggbokDag>(
      `${bok(siteId)}/forslag/${encodeURIComponent(proposalId)}/avvis`, body),

  avvik: (siteId: string, entryId: string, body: {
    on_date: string
    strakstiltak?: string | null
    rotaarsak?: string | null
    forebyggende?: string | null
    evaluering?: string | null
    category?: string | null
    alvorlighet?: number | null
    due_on?: string | null
    varslet?: boolean
    close?: boolean
  }) => api.post<LoggbokDag>(
    `${bok(siteId)}/${encodeURIComponent(entryId)}/avvik`, body),

  kommentar: (siteId: string, entryId: string,
              body: { on_date: string; body: string }) =>
    api.post<LoggbokDag>(
      `${bok(siteId)}/${encodeURIComponent(entryId)}/kommentar`, body),

  sett: (siteId: string, entryId: string, body: { on_date: string }) =>
    api.post<LoggbokDag>(
      `${bok(siteId)}/${encodeURIComponent(entryId)}/sett`, body),

  trekk: (siteId: string, entryId: string, body: { on_date: string }) =>
    api.post<LoggbokDag>(
      `${bok(siteId)}/${encodeURIComponent(entryId)}/trekk`, body),

  /** "Ferdig med runden." The row this writes is the evidence that the routine
   *  exists at all, which is why it is accepted with cards still open rather
   *  than being disabled until the screen is clean. */
  runde: (siteId: string, body: { on_date: string; note?: string | null }) =>
    api.post<LoggbokDag>(`${bok(siteId)}/runde`, body),

  signer: (siteId: string, body: { uke: string; note?: string | null }) =>
    api.post<{ schema: 1; signed: boolean }>(
      `${bok(siteId)}/uke/signer`, body),
}

/* ------------------------------------------------------------- lusedagbok */

/** Everything the Lusedagbok order screens start from, in one request.
 *
 *  The receiver owns every rule here -- who may order, which ring numbers are
 *  printed, what the generation is -- and this file only carries the answers.
 *  Each field is read with a default at the call site, per the README: a
 *  bundle deployed ahead of its receiver must not crash on a missing key. */
export interface LiceBookDraft {
  site: {
    site_id: string
    name: string
    locality_no: number | null
    company: string | null
  }
  /** What is in the water: the farm's own fish group when there is one, the
   *  register's guess (from IQ) otherwise. `source` says which, so the screen
   *  can say where the suggestion came from. */
  generation: {
    name: string | null
    origin: string | null
    source: 'farm' | 'register' | null
  }
  pens: { pen_id: string; pen_no: string; placed: boolean }[]
  /** The farm's QR code, once its first book has been made. */
  code: string | null
  sheets_default: number
  /** Drawn in the app by whoever asked for the book, until the farm has rings
   *  on the map of its own. A suggestion for staff to take. */
  app_drawing?: {
    request_id: string
    pens: { pen_no: string; lat: number; lon: number }[]
    created_at: string
  } | null
  /** Newest first, each with its orders newest first. */
  books: LiceBook[]
  /** Last time's address, so nobody types it twice. */
  delivery: LiceBookDelivery | null
  can_edit_layout: boolean
  /** False when the receiver has no printer set up. */
  available: boolean
}

export interface LiceBook {
  book_id: string
  created_at: string
  sheets: number
  pages: number
  generation: string | null
  hatchery: string | null
  /** Ring numbers as printed, in walking order. */
  pens: string[]
  counts: number
  /** How many preview pages the printer rendered. */
  previews: number
  orders: { order_id: string; qty: number; status: LiceBookStatus; created_at: string }[]
}

export interface LiceBookDelivery {
  recipient: string
  address: string
  postcode: string
  city: string
  phone: string | null
}

export type LiceBookStatus = 'received' | 'sent_to_print' | 'shipped' | 'cancelled'

export function liceBookPath(siteId: string) {
  return `/v1/sites/${encodeURIComponent(siteId)}/lice-book`
}

/** Freeze the farm as it is now and print it. Seconds, not milliseconds: a
 *  browser on the server sets every page. */
export function makeLiceBook(siteId: string, body: {
  generation?: string | null
  hatchery?: string | null
  sheets?: number
}) {
  return api.post<{ book: LiceBook; code: string }>(
    `/v1/sites/${encodeURIComponent(siteId)}/lice-books`, body, 90_000)
}

/** A page as the customer will hold it, rendered by the printer itself. */
export function liceBookPreview(siteId: string, bookId: string, n: number) {
  return `/v1/sites/${encodeURIComponent(siteId)}/lice-books/`
    + `${encodeURIComponent(bookId)}/preview/${n}`
}

export function liceBookPdf(siteId: string, bookId: string) {
  return `/v1/sites/${encodeURIComponent(siteId)}/lice-books/`
    + `${encodeURIComponent(bookId)}/pdf`
}

export function orderLiceBook(siteId: string, bookId: string,
                              body: LiceBookDelivery & { qty: number }) {
  return api.post<{ order_id: string; status: LiceBookStatus }>(
    `/v1/sites/${encodeURIComponent(siteId)}/lice-books/`
    + `${encodeURIComponent(bookId)}/orders`, body)
}

/** The QR on a printed book, resolved to its farm -- for somebody who may see
 *  it. Anybody else gets the same 404 as a code that does not exist. */
export function resolveBookCode(code: string) {
  return api.get<{ site_id: string }>(
    `/v1/book-codes/${encodeURIComponent(code)}`)
}

export interface LiceBookOrderRow {
  order_id: string
  book_id: string
  site_id: string
  site_name: string
  locality_no: number | null
  company: string | null
  qty: number
  recipient: string
  address: string
  postcode: string
  city: string
  phone: string | null
  status: LiceBookStatus
  created_at: string
  ordered_by: string | null
  pages: number
}

export function setLiceBookOrderStatus(orderId: string, status: LiceBookStatus) {
  return api.patch<{ order_id: string; status: LiceBookStatus }>(
    `/v1/lice-book-orders/${encodeURIComponent(orderId)}`, { status })
}
